import * as React from 'react'

import * as Sentry from '@sentry/browser'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { SaveUploadLogDocument } from '~/client/graph/operations/generated/FileUploader.generated'
import { SaveLocationsDocument } from '~/client/graph/operations/generated/Locations.generated'
import {
  SaveEquipmentsDocument,
  ValidateLocationRestrictionsForTimePeriodsDocument,
} from '~/client/graph/operations/generated/LocationType.generated'
import { SaveMaterialsDocument } from '~/client/graph/operations/generated/MaterialResources.generated'
import { DeleteFormsDocument } from '~/client/graph/operations/generated/SitePermits.generated'
import {
  BlockingReason,
  CalendricalType,
  IAttachment,
  IBlockedWorkflowGroup,
  IFormMaterial,
  ILocationInput,
  IMaterialInput,
  IMaterialProcurementData,
  IOffloadingEquipmentInput,
  IPermitTypeField,
  IPreciseLocation,
  IRecurringWorkflowCreationResult,
  ISignature,
  ISiteLocation,
  ISitePermit,
  IUploadLog,
  IUploadLogInput,
  LocationType,
  PermitFieldType,
  SitePermitStatus,
  SourceEntityType,
  WorkflowDurationType,
  WorkflowDurationUnit,
} from '~/client/graph/types.generated'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import WorkflowsStore from '~/client/src/shared/stores/domain/Workflows.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import PermitInspectionFieldsStore from '~/client/src/shared/stores/ui/PermitFieldsStores/PermitInspectionFields.store'
import PermitWorkflowFieldsStore from '~/client/src/shared/stores/ui/PermitFieldsStores/PermitWorkflowFields.store'
type FieldSelectionMode = 'selectExisting' | 'createNew'

import { IWarningNote } from '~/client/src/shared/components/WarningNote/WarningNote'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { ICompanyRestrictionSource } from '~/client/src/shared/utils/resourceRestrictionHelpers'
import {
  getPermittedCompaniesToName,
  isLocationCompanyRestricted,
} from '~/client/src/shared/utils/resourceRestrictionHelpers'

import { getWorkflowStepLevel } from '../../constants/formStatusesTags'
import {
  equipmentPermitFieldTypes,
  isLinkedWorkflowLikeField,
  locationOnlyPermitFieldTypes,
  locationPermitFieldTypes,
  materialResourcePermitFieldTypes,
} from '../../constants/PermitFieldTypeConstants'
import { ResourceTypeId } from '../../constants/resourceTypes'
import { splitResourceFormData } from '../../hooks/splitResourceFormData'
import Localization from '../../localization/LocalizationManager'
import IPermitFieldDto from '../../models/IPermitFieldDto'
import LocationAttributeBase from '../../models/LocationObjects/LocationAttributeBase'
import LocationBase from '../../models/LocationObjects/LocationBase'
import Material from '../../models/Material'
import PermitField from '../../models/PermitField'
import PermitInspection from '../../models/PermitInspection'
import PermitStatusChange from '../../models/PermitStatusChange'
import RecurringWorkflowSettings from '../../models/RecurringWorkflowSettings'
import UserProject from '../../models/UserProject'
import Workflow from '../../models/Workflow'
import { buildEquipmentInput } from '../../resources/domain/equipmentResourceTypeDefinition'
import {
  iconNameFromKind,
  isProjectLocationKind,
  LOCATION_KIND_PROPERTY,
  locationTypeFromKind,
} from '../../resources/domain/locationKind'
import {
  buildNewLocationInput,
  toLocationParentInput,
} from '../../resources/domain/locationResourceTypeDefinition'
import CompaniesStore from '../../stores/domain/Companies.store'
import { FileUploadingStore } from '../../stores/domain/FileUploading.store'
import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import MarkupsStore from '../../stores/domain/Markups.store'
import MaterialCategoriesStore from '../../stores/domain/MaterialCategories.store'
import MaterialsStore from '../../stores/domain/Materials.store'
import PermitInspectionChangesStore from '../../stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '../../stores/domain/PermitInspections.store'
import PermitStatusChangesStore from '../../stores/domain/PermitStatusChanges.store'
import ProjectMembersStore from '../../stores/domain/ProjectMembers.store'
import RecurringWorkflowSettingsStore from '../../stores/domain/RecurringWorkflowSettings.store'
import SitemapItemsStore from '../../stores/domain/SitemapItems.store'
import UserProjectsStore from '../../stores/domain/UserProjects.store'
import EventContext from '../../stores/EventStore/EventContext'
import EventsStore from '../../stores/EventStore/Events.store'
import PermitBallInCourtStore from '../../stores/ui/PermitBallInCourt.store'
import PermitFieldsBaseStore, {
  IPermitFormField,
} from '../../stores/ui/PermitFieldsStores/PermitFieldsBase.store'
import { SpecificPermitFieldTypes } from '../../stores/ui/PermitFieldsStores/PermitWorkflowFields.store'
import ProjectDateStore, {
  isAfter,
  isBefore,
} from '../../stores/ui/ProjectDate.store'
import { IWorkflowRestrictions } from '../../types/ILocationAttributeDto'
import type ILocationDisplayFallback from '../../types/ILocationDisplayFallback'
import IMsDateInterval from '../../types/IMsDateInterval'
import BsonObjectId from '../../utils/BsonObjectId'
import formatBytes from '../../utils/formatBytes'
import {
  buildProjectWeekWindowsFmt,
  formatOperatingIntervalFmt,
} from '../../utils/intervalFormatters'
import { NOOP } from '../../utils/noop'
import {
  isWeekdayScopedInterval,
  resolveOperatingIntervalIsDaily,
  withResolvedOperatingMode,
} from '../../utils/operatingIntervalMode'
import { withSingleDailyInterval } from '../../utils/operatingIntervals'
import { toast } from '../../utils/toast'
import { toUploadLogInput } from '../../utils/toUploadLogInput'
import { generateTraceId } from '../../utils/traceId'
import { DASH, EMPTY_STRING, NO_VALUE } from '../../utils/usefulStrings'
import { copyObject, customDebounce } from '../../utils/util'
import { durationToMilliseconds } from '../../utils/WorkflowDurationConverters'
import { advanceToNextWorkflowStep } from '../../utils/workflowStepUtils'
import {
  applyWorkflowTypeSwitchCarry,
  IWorkflowCarryPlan,
} from '../../utils/workflowTypeSwitchCarry'
import CalendarPlaceholderStore from '../CalendarDayView/components/CalendarPlaceholder.store'
import { FIELDS_AND_NOTE_SEPARATOR } from '../DeliveryDetails/DeliveryDetails.store'
import PermitTypeIcon from '../PermitTypeIcon/PermitTypeIcon'
import { buildWeekdayScheduleRows } from '../ResourceControls/intervalDefaults'
import { ITabObject } from '../Tabs/Tabs'
import TraceIdDisplay from '../TraceIdDisplay'
import {
  collectBlockedOverlaps,
  collectOverlapConflicts,
} from './components/PermitContentPickers/components/FormDropdownOptionsModal/components/overlapMessages'
import {
  explainOccurrence,
  getBlockedNote,
  getHoursNote,
  IClosureSpan,
  IExceededMaxDuration,
  IHoursOfOperationWarning,
  IOverlapConflict,
} from './hoursOfOperationWarning'
import ChangedFieldsMessageFormatter from './utils/ChangedFieldsMessageFormatter'
import { locationMapper } from './utils/changedFieldsMessageMappers'
import LocationClosureValidator from './utils/LocationClosureValidator'

const updateEvents = [
  e.GET_FORM_RELATED_CLOSURES,
  e.SAVE_SITE_PERMITS,
  e.SAVE_RECURRING_SITE_PERMITS,
  e.ADD_SITE_PERMIT_INSPECTION,
  e.UPDATE_SITE_PERMIT_INSPECTION,
  e.ADD_SITE_PERMIT_INSPECTION,
  e.MESSAGE_UPDATED,
  e.SAVE_MESSAGE,
  e.SAVE_WORKFLOW_MESSAGE,
  e.SAVE_SITE_PERMIT_FOLLOWINGS,
  e.DELETE_SITE_PERMIT_FOLLOWINGS,
]

const updateSuccessEvents = [
  e.SAVE_SITE_PERMITS_SUCCESS,
  e.MESSAGE_UPDATED,
  e.ADD_SITE_PERMIT_INSPECTION_SUCCESS,
  e.UPDATE_SITE_PERMIT_INSPECTION_SUCCESS,
]

const logLoadingEvents = [e.LOAD_AND_LISTEN_TO_SITE_PERMIT_STATUS_CHANGES]
const CURRENT_PERMIT_ID = 'current-permit-id'

interface IInspectionData {
  date: Date
  inspection: PermitInspection
}

export enum FormDropdownType {
  NONE,
  DEFAULT,
  MATERIAL,
  PROCUREMENT_ID,
  FORM_TYPE,
}

interface IFormDropdownOptionCard {
  title: string
  value: string
  isSearchable?: boolean
  hasBoldFont?: boolean
  location?: LocationBase
}

export interface IFormDropdownOption {
  title: string
  value: string
  cardOptions?: IFormDropdownOptionCard[]
  icon?: JSX.Element
  isSelected?: boolean
}

export enum FormDetailsTab {
  FORM = 'form',
  LOG = 'log',
}

interface IPendingUploadLog {
  workflowId: string
  uploadLog: IUploadLog
}

const MAX_INSPECTIONS_COUNT = 4
const MAX_MONTHS_VALUE = 12
const MAX_WEEKS_VALUE = 52
const MAX_DAYS_VALUE = 365
const MAX_DATE_RANGE_YEARS = 2
const LOADING_LOG_DELAY_MS = 700
const THRESHOLD_DEADLINE_HOURS = 1
const DEBOUNCE_DELAY = 500
const EMPTY_PERIODS: Array<{ startDate: number; endDate: number }> = []
const EMPTY_INTERVALS: IMsDateInterval[] = []
const EMPTY_OVERLAP_MAP: Map<string, IOverlapConflict> = new Map()
export const LINKED_WORKFLOW_START_LINE = 'Linked Workflow'

// Bookable resource types that ALSO live in the legacy LocationAttributesStore,
// whose legacy permittedCompanies is stale — route them to the fresh map and
// fail closed until it is ready, even before the id appears in that map.
// Materials are intentionally absent: they don't live in the legacy attr store,
// so a material is recognized as a bookable resource purely by its presence in
// the fresh map (a not-yet-loaded material is treated as unrestricted, like a
// pending resource). Add a legacy-backed bookable type here.
const FRESH_RESOURCE_LOCATION_TYPES: ReadonlySet<LocationType> = new Set([
  LocationType.OffloadingEquipment,
])

const toLocationDisplayFallback = ({
  formData,
  name,
}: {
  formData: Record<string, any>
  name: string
}): ILocationDisplayFallback => ({
  name,
  iconName:
    formData.iconName ?? iconNameFromKind(formData[LOCATION_KIND_PROPERTY]),
  color: formData.color ?? null,
  parentId: toLocationParentInput(formData.parent)?.parentId ?? null,
})

const getFieldName = (field: IPermitFormField) => {
  if (field.type === PermitFieldType.LinkedWorkflow) {
    return LINKED_WORKFLOW_START_LINE
  }
  return field.caption || field.type
}

export default class SitePermitCreationFormStore {
  @observable private attachmentUploadingCount: number = 0
  @observable public isNonWorkTimesModalOpened: boolean = false
  @observable public shouldShowNonWorkLabel: boolean = false
  @observable public shouldBlockSubmitButton: boolean = false
  @observable public isCapturingLocationScreenshots: boolean = false
  @observable public isTypeSelectorShown: boolean = false
  @observable public activeDetailsTab: FormDetailsTab = FormDetailsTab.FORM
  @observable public isReportDownloading = false
  @observable public isDeleteConfirmModalOpen = false
  @observable public isDeadlineModalShown = false
  @observable public isRecurringDatepickerShown = false
  @observable public isClosuresConflictModalShown = false
  @observable public hoursWarningDialogAction: 'submit' | 'approve' | null =
    null
  private resolveHoursWarningDialog: ((isConfirmed: boolean) => void) | null =
    null
  // From the moment an update is requested until it is dispatched — the awaits
  // in updatePermit leave room for a second tap to start a second save.
  @observable private isUpdateRequested = false
  @observable private shouldSkipClosureValidationOnce: boolean = false
  private pendingCreateArgs: { closeForm: () => void; isDraft: boolean } = null
  private pendingPickerApply: (() => void) | null = null

  // Track uploaded file URLs during creation to update upload logs later
  @observable private pendingUploadLogs: IPendingUploadLog[] = []
  @observable public linkedWorkflowsIds: string[] = []
  // A workflow created inside another form's field. It is saved with that form, which
  // never creates this store's new resources.
  public isNestedCreation = false

  @observable private createdLocationDisplayById = new Map<
    string,
    ILocationDisplayFallback
  >()
  @observable public pendingResources: Map<
    string,
    { formData: Record<string, any>; name: string; resourceTypeId: string }
  > = new Map()

  @observable public modalSearchValue: string = null
  @observable public currentPermitId: string = null
  // Stable client temp id markups buffer under while CREATING this workflow (it
  // has no real id yet). Flushed to the real id on create-success, discarded on
  // teardown. See WorkflowFormPreciseLocation + Markups.store.flushPending.
  // Minted per form, unless a map-first session handed its buffer over (see the ctor).
  public markupDraftId: string = BsonObjectId.generate()
  // Headless-host hooks (map-first composer): fired after a create resolves.
  public onCreateSuccess: (permits: ISitePermit[]) => void = null
  public onCreateError: () => void = null
  // Returns true to claim the change. Host-lifetime, so resetSelectedField leaves it.
  public onTypeChangeRequested: (typeId: string) => boolean = null

  // Registry for precise location screenshot capture-and-apply functions.
  // Each registered function captures its own mini map AND writes the result
  // directly to its own ISiteLocation.preciseLocation — no field matching
  // needed. Keyed by unique component instance key so table/repeated fields
  // each get their own entry.
  private preciseLocationCaptures = new Map<string, () => Promise<void>>()

  public registerPreciseLocationCapture(
    instanceKey: string,
    captureFn: () => Promise<void>,
  ) {
    this.preciseLocationCaptures.set(instanceKey, captureFn)
  }

  public unregisterPreciseLocationCapture(instanceKey: string) {
    this.preciseLocationCaptures.delete(instanceKey)
  }
  // Set once a create is dispatched: the create-success callback (async) will
  // flush the buffer, so teardown must NOT discard it first (the form closes
  // synchronously, before the callback runs).
  private markupBufferHandedOffForCreate = false
  // Set only when this form adopted a map-first buffer, so a failed create can hand those
  // markups back instead of destroying work the user did before the form existed.
  private adoptedMarkupDraftId: string = null
  // Consumed here so no later form adopts it; applied after the first seed rebuilds editablePermit.
  private adoptedCarryPlan: IWorkflowCarryPlan = null

  // Drafts are scoped by exact globe; opening on another one renders them nowhere.
  @computed
  public get adoptedMarkupGlobeId(): string {
    return this.adoptedMarkupDraftId
      ? this.markupsStore.getBySitePermitId(this.adoptedMarkupDraftId)[0]
          ?.globeViewId
      : null
  }
  @observable public debouncedModalSearchValue: string = null
  // Additional validation: overlapping/location restrictions
  @observable public areLocationRestrictionsViolated: boolean = false
  @observable.ref
  public overlapConflictsById: Map<string, IOverlapConflict> = EMPTY_OVERLAP_MAP

  // Fresh permittedCompanies (locationId -> company ids) for every bookable
  // resource type, sourced from the v2 queries and registered by
  // ResourceRestrictionsSync. The legacy LocationAttributesStore is stale for
  // these, so both the pickers and save validation read this map.
  // isFreshResourceDataReady is a coarse "some source has registered" flag, used
  // only by the company path (getCompanyRestrictionSource), which pairs it with a
  // per-id isInFreshMap check. It flips on the FIRST source to resolve, so it is
  // NOT a per-type readiness signal — private so callers can't gate a specific
  // resource type on it (that fails open for a still-loading sibling type). Gate
  // per type with areResourceControlsReadyForType instead.
  @observable public freshResourcePermittedCompanies: Map<string, string[]> =
    new Map()
  @observable private isFreshResourceDataReady: boolean = false

  // Unlike legacy-backed types (see FRESH_RESOURCE_LOCATION_TYPES), materials
  // have no legacy attr fallback, so a material still loading is absent from the
  // maps and reads as unrestricted (fail OPEN). isFreshResourceDataReady flips as
  // soon as the FIRST source (e.g. equipment) resolves, which isn't enough for
  // materials — track their query separately so save validation for a permit that
  // selects materials stays fail-closed until the materials query resolves.
  // Registered by ResourceRestrictionsSync.
  @observable public isMaterialResourceDataReady: boolean = false

  // Equipment is legacy-backed (see FRESH_RESOURCE_LOCATION_TYPES), so its
  // company dimension already fails closed by type. Its bookable / workflow-type
  // dimension does NOT: a selected equipment absent from resourceControlFieldsById
  // reads as available to isResourceUnavailable (i.e. valid), so
  // isFreshResourceDataReady flipping on the FIRST source (e.g. materials) isn't
  // enough. Track the equipment query separately and keep a permit that selects
  // equipment fail-closed until it resolves (query error / materials-first race).
  // Registered by ResourceRestrictionsSync.
  @observable public isEquipmentResourceDataReady: boolean = false

  // Bookable / workflow-type control fields per resource id, registered by
  // ResourceRestrictionsSync for every bookable resource type. The field
  // renderer and save validation have only ids, so they read control fields
  // from here.
  @observable public resourceControlFieldsById: Map<
    string,
    { isBookable: boolean }
  > = new Map()

  // Workflow restrictions (max booking duration) per resource id for resources
  // absent from the legacy LocationAttributesStore (materials). Registered by
  // ResourceRestrictionsSync; getExceededMaxBookingDurations falls back to it.
  @observable public resourceWorkflowRestrictionsById: Map<
    string,
    IWorkflowRestrictions
  > = new Map()

  // Hours of operation per resource id, registered by ResourceRestrictionsSync
  // for resource types that enforce operating hours (equipment). Used to warn
  // when a workflow's time period falls outside a resource's operating hours.
  @observable public resourceOperatingIntervalsById: Map<
    string,
    IMsDateInterval[]
  > = new Map()
  // Future intervals validation (recurring workflows)
  @observable public isFutureIntervalsRestrictionsWarningShown: boolean = false
  @observable public futureInvalidIntervals: Array<{
    startDate: number
    endDate: number
  }> = []

  @observable public selectedField: IPermitTypeField = null
  @observable public selectedFieldIndex: number = null
  @observable public selectedTableId: string = null
  @observable public selectedTableRowIndex: number = null
  @observable public selectedParentInstanceId: string = null
  @observable public selectedParentValueIndex: number = null
  @observable public selectedSubField: IPermitTypeField = null
  @observable public selectedFieldDropdownType: FormDropdownType =
    FormDropdownType.NONE
  @observable public selectedLocationType: LocationType = null
  @observable public fieldSelectionMode: FieldSelectionMode | null = null
  @observable public selectedOnChangeHandler: <T>(newValue: T) => void
  @observable public openWorkflowHandler: (workflow: Workflow) => void = null
  @observable.ref private permitBicStore: PermitBallInCourtStore = null
  @observable public isSignatureModalShown: boolean = false
  @observable public signatureInitialValue: ISignature = null
  private signatureFieldsStore: IPermitFieldsStore = null
  private signatureEntity: IPermitFieldsStore['editableEntity'] = null

  @observable private scrollToBottomFn: () => void = null
  private readonly collator = new Intl.Collator([], {
    numeric: true,
    sensitivity: 'accent',
  })

  public readonly workflowFieldsStore: PermitWorkflowFieldsStore
  public readonly inspectionFieldsStore: PermitInspectionFieldsStore

  private readonly changedFieldsMessageFormatter: ChangedFieldsMessageFormatter
  private readonly locationClosureValidator: LocationClosureValidator

  private logLoadingTimeoutId: NodeJS.Timeout

  private updateDebouncedSearchValue: (value: string) => void

  @computed
  public get isPermitUpdating(): boolean {
    return updateEvents.some(ev => this.state.loading.get(ev))
  }

  @computed
  public get isLoading(): boolean {
    return (
      this.isPermitUpdating ||
      this.isAttachmentUploading ||
      this.isReportDownloading ||
      this.isCapturingLocationScreenshots
    )
  }

  @computed
  public get isLogDataLoading(): boolean {
    return (
      !this.permitStatusChangesStore?.isDataReceived ||
      logLoadingEvents.some(ev => this.state.loading.get(ev))
    )
  }

  @computed
  public get editablePermit(): Workflow {
    return this.workflowFieldsStore.editableEntity
  }

  @computed
  public get existingRecurringSettings(): RecurringWorkflowSettings {
    return this.workflowFieldsStore.existingRecurringSettings
  }

  @computed
  public get editableRecurringSettings(): RecurringWorkflowSettings {
    return this.workflowFieldsStore.editableRecurringSettings
  }

  public get isRecurringSettingsValid(): boolean {
    return this.workflowFieldsStore.recurringOptionsField.isValid
  }

  public get isRecurringSettingsDateValid(): boolean {
    return this.workflowFieldsStore.isRecurringSettingsDateValid
  }

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly projectDateStore: ProjectDateStore,
    private readonly fileUploadingStore: FileUploadingStore,
    protected readonly permitTypesStore: WorkflowTypesStore,
    private readonly userProjectsStore: UserProjectsStore,
    private readonly projectMembersStore: ProjectMembersStore,
    private readonly companiesStore: CompaniesStore,
    private readonly locationAttributesStore: LocationAttributesStore,
    private readonly materialsStore: MaterialsStore,
    private readonly materialCategoryStore: MaterialCategoriesStore,
    private readonly tagsStore: TagsStore,
    private readonly graphExecutorStore: GraphExecutorStore,
    private readonly closuresStore: ClosuresStore,
    private readonly markupsStore: MarkupsStore,
    private readonly sitePermitsStore?: WorkflowsStore,
    private readonly permitStatusChangesStore?: PermitStatusChangesStore,
    private readonly permitInspectionsStore?: PermitInspectionsStore,
    private readonly permitInspectionChangesStore?: PermitInspectionChangesStore,
    private readonly calendarPlaceholderStore?: CalendarPlaceholderStore,
    private readonly recurringWorkflowSettingsStore?: RecurringWorkflowSettingsStore,
    private readonly sitemapItemsStore?: SitemapItemsStore,
    private readonly addToParentUploadLogMap?: (
      workflowId: string,
      uploadLog: IUploadLog,
    ) => void,
    private readonly addLinkedWorkflowsIds?: (workflowId: string) => void,
    private readonly onEditablePermitReset?: () => void,
  ) {
    // A map-first creation the composer could not finish routes here with its markups
    // already buffered; adopt that id so they belong to this form rather than being
    // stranded, and flush with it on create.
    const handedOverDraftId =
      this.eventsStore?.appState?.preSelectedMarkupDraftId
    if (handedOverDraftId) {
      this.markupDraftId = handedOverDraftId
      this.adoptedMarkupDraftId = handedOverDraftId
      // Consume it here so a later form can't adopt a stale buffer.
      this.eventsStore.appState.preSelectedMarkupDraftId = null
    }
    this.adoptedCarryPlan =
      this.eventsStore?.appState?.preSelectedWorkflowCarryPlan ?? null
    if (this.adoptedCarryPlan) {
      this.eventsStore.appState.preSelectedWorkflowCarryPlan = null
    }
    if (addToParentUploadLogMap) {
      this.currentPermitId = BsonObjectId.generate()
      this.addLinkedWorkflowsIds?.(this.currentPermitId)
    }
    this.workflowFieldsStore = new PermitWorkflowFieldsStore(
      this.eventsStore,
      this.projectDateStore,
      this.permitTypesStore,
      this.companiesStore,
      this.recurringWorkflowSettingsStore,
      this.locationAttributesStore,
      this.sitemapItemsStore,
      this.closuresStore,
    )
    this.inspectionFieldsStore = new PermitInspectionFieldsStore(
      this.eventsStore,
      this.workflowFieldsStore,
      this.locationAttributesStore,
    )
    this.changedFieldsMessageFormatter = new ChangedFieldsMessageFormatter(
      this.projectMembersStore,
      this.companiesStore,
      this.locationAttributesStore,
      this.materialsStore,
      this.projectDateStore,
      this.getLocationName,
    )

    this.locationClosureValidator = new LocationClosureValidator(
      this.eventsStore.appState,
      this.closuresStore,
    )

    makeObservable(this)

    // After makeObservable: before it, this is the unbound prototype method.
    if (handedOverDraftId) {
      this.eventsStore.appState.applyHandedOffPreciseLocation =
        this.applyPreciseLocationFromMap
    }

    this.updateDebouncedSearchValue = customDebounce((value: string) => {
      this.debouncedModalSearchValue = value
    }, DEBOUNCE_DELAY)
  }

  public extendLinkedWorkflowsIds = (workflowId: string) => {
    this.linkedWorkflowsIds.push(workflowId)
  }

  @computed
  public get shouldShowInspectionPopup(): boolean {
    return (
      !!this.inspectionFieldsStore.editableEntity && !!this.editablePermit.id
    )
  }

  @computed
  public get statusChanges(): PermitStatusChange[] {
    return this.permitStatusChangesStore.getStatusChangesForPermit(
      this.editablePermit?.id,
    )
  }

  @computed
  public get isLastStep(): boolean {
    return this.template?.isLastStep(this.existingPermit.currentWorkflowStepId)
  }

  public getLatestInspectionChangeByDate = (
    date: number | Date,
  ): PermitInspection => {
    const { isSameDay } = this.projectDateStore
    const { id: permitId } = this.editablePermit
    const inspection = this.permitInspectionsStore
      .getInspectionsForPermit(permitId)
      .find(insp => isSameDay(insp.inspectionDate, date))

    if (!inspection) {
      return null
    }

    const latestChange =
      this.permitInspectionChangesStore.getLatestChangeForInspection(
        inspection.id,
      )

    return latestChange?.toInspection(permitId) || inspection
  }

  @computed
  public get permitInspectionData(): IInspectionData[] {
    if (!this.isInspectionPermit) {
      return []
    }

    const { isSameDay, startOfDay } = this.projectDateStore
    const { id, endDate, getAllInspectionDates } = this.editablePermit

    const now = Date.now()
    const endDateToUse = isAfter(endDate, now) ? now : endDate
    const inspectionDates = getAllInspectionDates(
      this.permitTypesStore,
      this.projectDateStore,
      endDateToUse,
    )
    const permitInspections =
      this.permitInspectionsStore.getInspectionsForPermit(id)
    const existingInspectionDates = permitInspections.map(pi =>
      startOfDay(pi.inspectionDate),
    )
    const allDates = inspectionDates
      .concat(existingInspectionDates)
      .sort((a, b) => a.getTime() - b.getTime())

    return allDates.reduce((list, dt) => {
      if (list.find(inspd => isSameDay(inspd.date, dt))) {
        return list
      }

      const inspection = permitInspections.find(({ inspectionDate }) =>
        isSameDay(inspectionDate, dt),
      )

      if (!inspection) {
        list.push({ date: dt, inspection: null })
        return list
      }

      const latest =
        this.permitInspectionChangesStore.getLatestChangeForInspection(
          inspection.id,
        )

      list.push({
        date: dt,
        inspection: latest?.toInspection(inspection.permitId) || inspection,
      })
      return list
    }, [] as IInspectionData[])
  }

  public get permitTypes(): WorkflowType[] {
    return this.permitTypesStore.enabledTypes
  }

  public get isInspectionPermit(): boolean {
    return this.template?.isInspectionType
  }

  public get inspectionOptions() {
    return this.template?.inspectionOptions
  }

  public get inspectionFrequency(): number {
    return this.editablePermit?.getInspectionFrequency(this.template)
  }

  public get inspectionFrequencyType() {
    return this.editablePermit?.getInspectionFrequencyType(this.template)
  }

  public get isEmissionFormType(): boolean {
    return this.template?.isEmissionFormType
  }

  public get selectedDaysToRepeat(): number[] {
    return this.editablePermit?.getSelectedDaysToRepeat(this.template)
  }

  public get shouldShowDaysToRepeat(): boolean {
    return this.inspectionFrequencyType === CalendricalType.Week
  }

  public get isBeforeDeadlineEnds(): boolean {
    if (!this.isInspectionPermit || this.editablePermit.isDoneOrDenied) {
      return false
    }

    if (this.isAdminOrFormsMaster) {
      return true
    }

    const { addHours, startOfDay } = this.projectDateStore
    const { deadlineTime } = this.inspectionOptions

    const now = Date.now()
    const deadlineDate = addHours(startOfDay(now), deadlineTime)

    return isBefore(now, deadlineDate)
  }

  public get isNewInspectionAllowed(): boolean {
    if (!this.isInspectionPermit || this.editablePermit.isDoneOrDenied) {
      return false
    }

    return this.isTodayInspectionDay && this.isBeforeDeadlineEnds
  }

  public get isInspectionDeadlineEnded(): boolean {
    if (!this.isInspectionPermit || this.editablePermit.isDoneOrDenied) {
      return false
    }

    return this.isTodayInspectionDay && !this.isBeforeDeadlineEnds
  }

  public get isTodayInspectionDay(): boolean {
    if (!this.isInspectionPermit || this.editablePermit.isDoneOrDenied) {
      return false
    }

    return this.projectDateStore.isSameDay(
      this.currentOrPreviousInspectionDate,
      Date.now(),
    )
  }

  public get currentOrPreviousInspectionDate(): Date {
    if (!this.isInspectionPermit || this.editablePermit.isDoneOrDenied) {
      return null
    }

    return this.editablePermit.getLatestInspectionDate(
      this.permitTypesStore,
      this.projectDateStore,
    )
  }

  public get nextInspectionDates(): Date[] {
    if (!this.isInspectionPermit || this.editablePermit.isDoneOrDenied) {
      return null
    }

    const now = new Date()

    return this.editablePermit
      .getAllInspectionDates(this.permitTypesStore, this.projectDateStore)
      .filter(dt => dt > now)
      .filter((_, i) => i < MAX_INSPECTIONS_COUNT)
  }

  public get shouldBlockOnNonWorkTimes(): boolean {
    return this.template?.shouldBlockOnNonWorkTimes
  }

  public get isLastInspectionDay(): boolean {
    return this.workflowFieldsStore.isLastInspectionDay
  }

  public get template(): WorkflowType {
    return this.workflowFieldsStore.template
  }

  public get isActualTypeVersion(): boolean {
    return (
      this.template?.id &&
      this.workflowFieldsStore.actualPermiTypes.some(
        type => type.id === this.template.id,
      )
    )
  }

  @action.bound
  public setScrollToBottomFn(fn: () => void) {
    this.scrollToBottomFn = fn
  }

  @action.bound
  public scrollToBottom() {
    this.scrollToBottomFn?.()
  }

  public get isFormEditAllowedForUser(): boolean {
    return (
      this.isAdminOrFormsMaster ||
      this.isWorkflowCreator ||
      this.existingPermit?.isRequesterOrResponsible(this.state.user.id)
    )
  }

  @computed
  public get isBicUserForCurrentStep(): boolean {
    const { existingPermit, template, permitBicStore } = this
    if (!template || !permitBicStore) return false
    const { currentWorkflowStepId } = existingPermit
    const stepRuleIds = template.getStepRuleIds(currentWorkflowStepId)
    if (!stepRuleIds?.length) return false
    return permitBicStore.isUserAllowedByBicRules(
      this.state.user.id,
      existingPermit,
      stepRuleIds,
      template.getStepCompletionMode(currentWorkflowStepId),
    )
  }

  public get isInspectionEditAllowedForUser(): boolean {
    return this.isFormEditAllowedForUser || this.isBicUserForCurrentStep
  }

  public get isPermitDatesFieldSelected(): boolean {
    return this.selectedField?.type === PermitFieldType.StartFinishDates
  }

  public get selectedFieldValues(): any[] {
    const fieldsStore = this.shouldShowInspectionPopup
      ? this.inspectionFieldsStore
      : this.workflowFieldsStore

    return (
      fieldsStore.getFieldValues(
        this.selectedField?.id,
        this.selectedTableId,
        this.selectedTableRowIndex,
        false,
        this.selectedParentInstanceId,
        this.selectedParentValueIndex,
      ) || []
    )
  }

  public get parentFieldValues(): any[] {
    if (!this.selectedParentInstanceId) {
      return []
    }

    const fieldsStore = this.shouldShowInspectionPopup
      ? this.inspectionFieldsStore
      : this.workflowFieldsStore

    const parentField = fieldsStore.editableEntity.fields.find(
      f => f.instanceId === this.selectedParentInstanceId,
    )

    if (!parentField) {
      return []
    }

    return (
      fieldsStore.getFieldValues(
        parentField.fieldId,
        null,
        null,
        false,
        parentField.parentInstanceId,
        parentField.parentValueIndex,
      ) || []
    )
  }

  /** Re-validates itself: changeFieldValue skips the location hook. Returns the map-view Location to ask about. */
  @action.bound
  public applyPreciseLocationFromMap(
    preciseLocation: IPreciseLocation,
  ): LocationBase | null {
    const offer = this.workflowFieldsStore.applyPinMove(preciseLocation)
    this.scheduleLocationsValidation()
    return offer
  }

  /** Host-called on mount, after dates and a duplicate land: the seed snapped without them. */
  @action.bound
  public resnapSeededPin() {
    this.workflowFieldsStore.snapSeededPin()
    this.scheduleLocationsValidation()
  }

  /** Host-called, right after the first seed: a re-seed would undo it. */
  @action.bound
  public applyAdoptedCarryPlan() {
    const plan = this.adoptedCarryPlan
    if (!plan) {
      return
    }
    this.adoptedCarryPlan = null
    applyWorkflowTypeSwitchCarry(
      this.editablePermit,
      this.template,
      plan,
      this.workflowFieldsStore,
    )
  }

  @action.bound
  public setDefaultSitePermit(
    permitToEdit?: Workflow,
    typeId?: string,
    isLastStep?: boolean,
    prepopulatedFields?: IPermitFieldDto[],
    preSelectedSiteLocation?: ISiteLocation,
  ) {
    this.workflowFieldsStore.setDefaultSitePermit(
      permitToEdit,
      typeId,
      isLastStep,
      prepopulatedFields,
      preSelectedSiteLocation,
    )
    if (!permitToEdit) {
      this.inspectionFieldsStore.setEmptyInspection()
    }

    // Validate locations for pre-populated forms (locations/dates may already be set)
    this.scheduleLocationsValidation()
  }

  @action.bound
  public setSelectedField(
    typeField: IPermitTypeField,
    index?: number,
    tableId?: string,
    tableRowIndex?: number,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    this.selectedField = typeField
    this.selectedFieldIndex = index

    this.selectedTableId = tableId
    this.selectedTableRowIndex = tableRowIndex

    this.selectedParentInstanceId = parentInstanceId
    this.selectedParentValueIndex = parentValueIndex
  }

  @action.bound
  public resetSelectedField() {
    this.selectedField = null
    this.selectedFieldIndex = null
    this.selectedTableId = null
    this.selectedTableRowIndex = null
    this.selectedParentInstanceId = null
    this.selectedParentValueIndex = null

    this.setSelectedSubField(null)
    this.setSelectedFieldDropdownType(FormDropdownType.NONE)
    this.setSelectedLocationType(null)
    this.setSelectedOnChangeHandler(null)
    this.fieldSelectionMode = null
  }

  @action.bound
  public setFieldSelectionMode(mode: FieldSelectionMode | null) {
    this.fieldSelectionMode = mode
  }

  @action.bound
  public setSelectedSubField(typeSubField: IPermitTypeField) {
    this.selectedSubField = typeSubField
  }

  @action.bound
  public setSelectedFieldDropdownType(dropdownType: FormDropdownType) {
    this.selectedFieldDropdownType = dropdownType || FormDropdownType.NONE
  }

  @action.bound
  public setSelectedLocationType(locationType: LocationType) {
    this.selectedLocationType = locationType
  }

  @action.bound
  public setSelectedOnChangeHandler(onChangeHandler: <T>(newValue: T) => void) {
    this.selectedOnChangeHandler = onChangeHandler
  }

  @action.bound
  public addPendingResource(
    tempId: string,
    formData: Record<string, any>,
    name: string,
    resourceTypeId: string,
  ) {
    this.pendingResources.set(tempId, { formData, name, resourceTypeId })
  }

  @action.bound
  public removePendingResource(tempId: string) {
    this.pendingResources.delete(tempId)
  }

  public getLocationDisplayFallback(
    id: string | null | undefined,
  ): ILocationDisplayFallback | null {
    if (!id) return null
    const pending = this.pendingResources.get(id)
    if (pending?.resourceTypeId === ResourceTypeId.LOCATIONS) {
      return toLocationDisplayFallback(pending)
    }
    return this.createdLocationDisplayById.get(id) ?? null
  }

  public getLocationName = (location: ISiteLocation): string | undefined =>
    locationMapper(location, this.locationAttributesStore) ||
    this.getLocationDisplayFallback(location?.id)?.name

  // Inspections and nested workflows are saved without creating the form's new resources.
  public get canCreatePendingResources(): boolean {
    return !this.shouldShowInspectionPopup && !this.isNestedCreation
  }

  // The project has one site root; the form never creates another.
  public getPendingLocationType(tempId: string): LocationType | null {
    const pending = this.pendingResources.get(tempId)
    if (pending?.resourceTypeId !== ResourceTypeId.LOCATIONS) return null

    const kind = pending.formData[LOCATION_KIND_PROPERTY]
    return isProjectLocationKind(kind) ? null : locationTypeFromKind(kind)
  }

  @action.bound
  public setOpenWorkflowHandler(handler: (workflow: Workflow) => void) {
    this.openWorkflowHandler = handler
  }

  @action.bound
  public setPermitBicStore(permitBicStore: PermitBallInCourtStore) {
    this.permitBicStore = permitBicStore
  }

  @action.bound
  public changeSelectedFieldValue<T>(newValue: T, shouldSkipReset?: boolean) {
    const fieldsStore = this.shouldShowInspectionPopup
      ? this.inspectionFieldsStore
      : this.workflowFieldsStore
    // Trigger location restrictions validation when a location/equipment or
    // bookable material-resource field changes (materials are validated too).
    const changedType = this.selectedField?.type || this.selectedSubField?.type
    if (
      locationPermitFieldTypes.includes(changedType) ||
      materialResourcePermitFieldTypes.includes(changedType)
    ) {
      this.scheduleLocationsValidation()
    }

    if (this.selectedOnChangeHandler) {
      this.selectedOnChangeHandler(newValue)
    } else if (this.selectedTableId) {
      fieldsStore.changeTableFieldValue(
        this.selectedTableId,
        this.selectedTableRowIndex,
        this.selectedField,
        this.selectedFieldIndex,
        newValue,
        this.selectedParentInstanceId,
        this.selectedParentValueIndex,
      )
    } else {
      fieldsStore.changeFieldValue(
        this.selectedField,
        this.selectedFieldIndex,
        newValue,
        this.selectedParentInstanceId,
        this.selectedParentValueIndex,
      )
    }

    if (!shouldSkipReset) {
      this.resetSelectedField()
    }
  }

  @action.bound
  public updateSearchValue(newValue: string) {
    this.modalSearchValue = newValue
    this.updateDebouncedSearchValue(newValue)
  }

  @action.bound
  public applyDates(newStartDate: Date, newEndDate: Date, isAllDay?: boolean) {
    if (!newStartDate || !newEndDate) return

    const isAllDayPermit = !!isAllDay
    this.editablePermit.startDate = newStartDate.getTime()
    this.editablePermit.endDate = newEndDate.getTime()
    this.editablePermit.isAllDay = isAllDayPermit

    if (this.calendarPlaceholderStore?.eventPlaceholder) {
      this.calendarPlaceholderStore.setEventPlaceholderTime(
        newStartDate,
        newEndDate,
        isAllDayPermit,
      )
    }

    this.unblockAndOpenModalsIfNeed()

    // Trigger location restrictions validation when dates change
    this.scheduleLocationsValidation()
  }

  @action.bound
  public applySpecificInspectionFrequency(newFrequency: number) {
    if (!this.isInspectionPermit) {
      return
    }

    this.createInspectionOptionsIfNotExists()

    const { specificInspectionOptions: options } = this.editablePermit

    options.inspectionFrequency =
      newFrequency > this.inspectionMaxNumber
        ? this.inspectionMaxNumber
        : newFrequency
  }

  @action.bound
  public applySpecificInspectionFrequencyType(
    newFrequencyType: CalendricalType,
  ) {
    if (!this.isInspectionPermit) {
      return
    }

    this.createInspectionOptionsIfNotExists()

    const { specificInspectionOptions: options } = this.editablePermit

    options.inspectionFrequencyType = newFrequencyType

    if (options.inspectionFrequency > this.inspectionMaxNumber) {
      return this.applySpecificInspectionFrequency(this.inspectionMaxNumber)
    }
  }

  @action.bound
  public changeDayToRepeat(dayNumber: number) {
    if (!this.isInspectionPermit) {
      return
    }

    this.createInspectionOptionsIfNotExists()

    const { specificInspectionOptions } = this.editablePermit

    if (!specificInspectionOptions.selectedDaysToRepeat) {
      specificInspectionOptions.selectedDaysToRepeat = []
    }

    const index = specificInspectionOptions.selectedDaysToRepeat.findIndex(
      day => day === dayNumber,
    )
    if (index === -1) {
      specificInspectionOptions.selectedDaysToRepeat.push(dayNumber)
    } else {
      specificInspectionOptions.selectedDaysToRepeat.splice(index, 1)
    }
  }

  @action.bound
  public setIsReportDownloading() {
    this.isReportDownloading = true
  }

  @action.bound
  public resetIsReportDownloading() {
    this.isReportDownloading = false
  }

  @action.bound
  public toggleDeleteConfirmModal() {
    this.isDeleteConfirmModalOpen = !this.isDeleteConfirmModalOpen
  }

  public onPermitTypeChanged = (typeId: string) => {
    if (this.editablePermit?.id) return
    // The re-seed below passes no location, falling back to a seed the map may have nulled.
    if (this.onTypeChangeRequested?.(typeId)) return

    this.inspectionFieldsStore.deselectInspection()
    this.setDefaultSitePermit(null, typeId)

    this.hideNonWorkTimeLabel()
    this.unblockSubmitButton()

    const { startDate, endDate, isAllDay } =
      this.calendarPlaceholderStore?.eventPlaceholder || {}
    this.applyDates(startDate, endDate, isAllDay)
    this.resnapSeededPin()

    if (!startDate || !endDate) {
      this.unblockAndOpenModalsIfNeed()
    }

    this.onEditablePermitReset?.()
  }

  public uploadAttachment = async (
    file: File,
    typeField: IPermitTypeField,
    fieldsStore: IPermitFieldsStore,
    parentInstanceId: string,
    parentValueIndex: number,
  ): Promise<boolean> => {
    if (!file) {
      return false
    }

    const attachment = await this.uploadPermitFile(file)
    if (!attachment) {
      return false
    }

    fieldsStore.addAttachmentValue(
      attachment,
      typeField,
      parentInstanceId,
      parentValueIndex,
    )
    return true
  }

  public replaceAttachment = async (
    file: File,
    typeField: IPermitTypeField,
    fieldsStore: IPermitFieldsStore,
    fieldIndex: number,
    parentInstanceId: string,
    parentValueIndex: number,
  ) => {
    const attachment = await this.uploadPermitFile(file)
    if (!attachment) {
      throw new Error('Attachment upload returned no file')
    }

    fieldsStore.changeFieldValue(
      typeField,
      fieldIndex,
      attachment,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private uploadPermitFile = async (file: File): Promise<IAttachment> => {
    this.setAttachmentUploading()

    try {
      const mainLocation = this.editablePermit.locations?.[0]

      const [result] = await this.fileUploadingStore.uploadFile(
        file,
        this.fileUploadingStore.getAttachmentFileType(file),
        this.eventsStore.appState.activeProject.id,
        file.name,
        false,
        this.editablePermit.id,
        SourceEntityType.Form,
        this.editablePermit.locations?.map(l => l.id),
        this.editablePermit.code,
        this.editablePermit.getNameOfPermitType(this.permitTypesStore),
        mainLocation?.id ? this.getLocationName(mainLocation) : undefined,
        this.editablePermit.isDraft,
      )

      if (!result) {
        return null
      }

      // Track upload log ID if permit doesn't have an ID yet (creation mode)
      this.addToUpdateLogList(result.uploadLog)

      return {
        fileName: file.name,
        size: formatBytes(file.size),
        url: result.fileURL,
      }
    } finally {
      this.resetAttachmentUploading()
    }
  }

  @action.bound
  public addToUpdateLogList(log: IUploadLog) {
    if (this.editablePermit?.id || !log) return

    if (this.addToParentUploadLogMap) {
      this.addToParentUploadLogMap(this.currentPermitId, log)
    } else {
      this.pendingUploadLogs.push({
        workflowId: CURRENT_PERMIT_ID,
        uploadLog: log,
      })
    }
  }

  // Single source of truth for resource save-gating: admins/forms-master always
  // pass; members pass only when no location restriction is violated. That flag
  // already subsumes invalid (non-bookable / wrong-workflow-type) selections —
  // they're folded into the gating set in applyViolations — so no separate check
  // is needed. Shared by the create/edit getters and the save-time check.
  //
  // Materials are the exception: they fail OPEN by id (no legacy attr fallback),
  // so save gating for a permit that selects materials needs two guards:
  //   1. Until the materials query resolves, a still-loading material is absent
  //      from the maps and reads as unrestricted — stay fail-closed.
  //   2. Once loaded, block an invalid material synchronously. areLocationRestric-
  //      tionsViolated is applied by an async re-validation, so between the
  //      controls arriving and that validation completing it would be stale-false
  //      and briefly open the gate; the synchronous check below closes that gap.
  @computed
  private get areResourceSelectionsSubmittable(): boolean {
    if (this.isAdminOrFormsMaster) return true
    if (this.areLocationRestrictionsViolated) return false
    if (
      this.materialResourceLocationIdSet.size > 0 &&
      !this.isMaterialResourceDataReady
    ) {
      return false
    }
    // Equipment analogue of the materials guard, but narrower: blocks only
    // equipment still absent from the control map (see hasUnreadyEquipmentSelection).
    if (this.hasUnreadyEquipmentSelection) return false
    return !this.hasLoadedMaterialRestrictionViolation
  }

  // Whether a location is a legacy-backed bookable resource (equipment): a legacy
  // attr whose type is in FRESH_RESOURCE_LOCATION_TYPES. Single source of truth for
  // the company gate (getCompanyRestrictionSource), the equipment readiness gate, and
  // which locations read their operating hours from the v2 source rather than the
  // legacy attr; zones/gates and materials (no legacy attr) are excluded.
  private isLegacyBackedBookableResource(locationId: string): boolean {
    const attr = this.locationAttributesStore.getById(
      locationId,
    ) as LocationBase
    return !!attr?.type && FRESH_RESOURCE_LOCATION_TYPES.has(attr.type)
  }

  // Any selected equipment while its v2 controls haven't settled. Mirrors the
  // materials guard: a still-loading / errored query — including cache-and-network
  // serving a STALE prior control map (the map isn't cleared on refetch) — can't
  // be trusted, so fail closed for any selected legacy-backed equipment,
  // regardless of a stale map entry. Once ready, the async validation
  // (areLocationRestrictionsViolated) takes over and an id absent from the map is
  // a genuinely deleted/temp resource treated as valid.
  @computed
  private get hasUnreadyEquipmentSelection(): boolean {
    if (this.isEquipmentResourceDataReady) return false
    return this.allRestrictionLocationIds.some(id =>
      this.isLegacyBackedBookableResource(id),
    )
  }

  // Selected materials whose controls have loaded but are non-bookable /
  // off-workflow-type / company-restricted, evaluated synchronously from the
  // registered maps (the same checks the async restriction validation uses).
  // Not-yet-loaded ids are deferred to the readiness gate above; ids absent after
  // load (deleted/temp) are treated as valid, matching isResourceUnavailable.
  @computed
  private get hasLoadedMaterialRestrictionViolation(): boolean {
    for (const id of this.materialResourceLocationIdSet) {
      if (!this.resourceControlFieldsById.has(id)) continue
      if (this.isResourceUnavailable(id)) return true
      if (this.isLocationRestrictedForCurrentCompanies(id)) return true
    }
    return false
  }

  @computed
  public get isPermitValid(): boolean {
    return (
      this.workflowFieldsStore.areAllFieldsValid &&
      !this.shouldBlockSubmitButton &&
      this.areResourceSelectionsSubmittable
    )
  }

  @computed
  public get canSaveWorkflow(): boolean {
    return (
      this.isPermitValid &&
      !this.isAnyDialogOpened &&
      !this.isAttachmentUploading
    )
  }

  @computed
  public get isChangedPermitValid(): boolean {
    return (
      !this.shouldBlockSubmitButton &&
      this.areResourceSelectionsSubmittable &&
      this.workflowFieldsStore.areChangedFieldsValid &&
      !this.isAttachmentUploading
    )
  }

  public get isAnyFieldInvalid(): boolean {
    return this.workflowFieldsStore.isAnyFieldInvalid
  }

  public get isDraftButtonEnabled(): boolean {
    return (
      !this.isAnyDialogOpened &&
      !this.isAnyFieldInvalid &&
      !this.isAttachmentUploading
    )
  }

  /**
   * Calls all registered capture-and-apply functions in parallel.
   * Each function captures its own mini map screenshot and writes imageUrl
   * directly to its own ISiteLocation — no field matching needed.
   */
  private async captureLocationScreenshots(): Promise<void> {
    if (this.preciseLocationCaptures.size === 0) {
      return
    }

    this.isCapturingLocationScreenshots = true
    try {
      await Promise.all(
        Array.from(this.preciseLocationCaptures.values()).map(fn => fn()),
      )
    } finally {
      this.isCapturingLocationScreenshots = false
    }
  }

  @action.bound
  public prepareForCreate(isDraft: boolean = false) {
    this.workflowFieldsStore.setInitialStatus(isDraft)
    this.setNextWorkflowStep()

    this.editablePermit.fields = this.editablePermit.fields.filter(f =>
      this.workflowFieldsStore.fields.some(pf => pf.id === f.fieldId),
    )

    return [
      this.editablePermit,
      ...this.extractChildWorkflows(isDraft),
    ].reverse()
  }

  @action.bound
  public prepareForUpdate(skipStepAdvancement: boolean = false) {
    // A save that stays on the step, as a sign-off the step still waits on, sets no status, so
    // it keeps the level of the one it has: a new level would read as the step being entered again
    if (!skipStepAdvancement) {
      this.setWorkflowStepLevel()
      this.setNextWorkflowStep()
    }

    this.editablePermit.fields = this.editablePermit.fields.filter(f =>
      this.workflowFieldsStore.fields.some(pf => pf.id === f.fieldId),
    )

    // When requireAllSubmissions is enabled, exclude current step fields from base fields
    // They are stored in stepSubmissions instead
    const { currentWorkflowStepId } = this.existingPermit
    const requireAllSubmissions = this.template?.getStepRequireAllSubmissions(
      currentWorkflowStepId,
    )

    if (requireAllSubmissions) {
      const currentStepFieldIds = this.workflowFieldsStore.currentStepFieldIds
      this.editablePermit.fields = this.editablePermit.fields.filter(
        f => !currentStepFieldIds.has(f.fieldId),
      )
    }

    this.workflowFieldsStore.resetValidationState()

    return [this.editablePermit, ...this.extractChildWorkflows()].reverse()
  }

  private createRecurringPermit = async (
    permitsToSave: Workflow[],
    closeForm?: () => void,
  ) => {
    const [mainPermit, ...childPermits] = permitsToSave

    this.eventsStore.dispatch(
      e.SAVE_RECURRING_SITE_PERMITS,
      mainPermit,
      this.workflowFieldsStore.recurringSettings,
      this.handlePermitCreateSuccess,
      this.handleCreateError,
    )

    if (childPermits.length) {
      this.eventsStore.dispatch(e.SAVE_SITE_PERMITS, childPermits, () => {
        this.resetRecurringSettingsIfNeed()
      })
    }

    this.markupBufferHandedOffForCreate = true
    closeForm?.()
  }

  public saveDraftAsRequested = () => {
    if (!this.canSaveWorkflow) return

    if (this.editablePermit.recurringSettingsId) {
      this.setRecurringSettings(this.editableRecurringSettings)
    }

    this.createPermit(NOOP)
  }

  public saveDraftWorkflow = (closeForm: () => void) => {
    if (!this.isDraftButtonEnabled) return

    if (this.editablePermit.id && this.editablePermit.recurringSettingsId) {
      this.setRecurringSettings(this.editableRecurringSettings)
    }

    this.createPermit(closeForm, true)
  }

  private static readonly RESOURCE_FIELD_CONFIGS: Array<{
    types: PermitFieldType[]
    accessor: string
  }> = [
    {
      types: [
        PermitFieldType.Equipment,
        PermitFieldType.EquipmentInformational,
      ],
      accessor: 'id',
    },
    {
      types: [
        PermitFieldType.MaterialResource,
        PermitFieldType.MaterialResourceInformational,
      ],
      accessor: 'locationId',
    },
    { types: locationOnlyPermitFieldTypes, accessor: 'id' },
  ]

  private forEachResourceFieldValue(
    callback: (value: any, accessor: string, field: any, index: number) => void,
  ) {
    for (const {
      types,
      accessor,
    } of SitePermitCreationFormStore.RESOURCE_FIELD_CONFIGS) {
      const fields = types.flatMap(t =>
        this.editablePermit.getAllFieldsByType(t),
      )
      fields.forEach(field => {
        field.values.forEach((value: any, index: number) => {
          if (Array.isArray(value)) {
            value.forEach((v: any) => callback(v, accessor, field, index))
          } else {
            callback(value, accessor, field, index)
          }
        })
      })
    }
  }

  @action.bound
  public releasePendingResourceIfUnreferenced(tempId: string) {
    if (
      this.pendingResources.has(tempId) &&
      !this.getReferencedPendingIds().has(tempId)
    ) {
      this.pendingResources.delete(tempId)
    }
  }

  private getReferencedPendingIds(): Set<string> {
    const referenced = new Set<string>()
    this.forEachResourceFieldValue((value, accessor) => {
      const id = value?.[accessor]
      if (id && this.pendingResources.has(id)) {
        referenced.add(id)
      }
    })
    return referenced
  }

  private async materializePendingResources(): Promise<boolean> {
    if (this.pendingResources.size === 0) return true

    const projectId = this.state.activeProject?.id
    if (!projectId) return false

    // Only materialize pending resources still referenced by permit fields
    const referencedIds = this.getReferencedPendingIds()
    if (referencedIds.size === 0) {
      runInAction(() => this.pendingResources.clear())
      return true
    }

    const locationInputs: ILocationInput[] = []
    const equipmentInputs: IOffloadingEquipmentInput[] = []
    const materialInputs: IMaterialInput[] = []

    for (const [tempId, { formData, name, resourceTypeId }] of this
      .pendingResources) {
      if (!referencedIds.has(tempId)) continue

      if (resourceTypeId === ResourceTypeId.LOCATIONS) {
        const input = buildNewLocationInput(
          projectId,
          { ...formData, name },
          tempId,
        )
        // A kind with no legacy store saves a value nothing can display or validate.
        if (!this.getPendingLocationType(tempId)) {
          toast.error(Localization.translator.locationTypeNotUsableInWorkflows)
          return false
        }
        // Without a parent the backend silently puts the location under the site root.
        if (!input.parent) {
          toast.error(Localization.translator.newLocationNeedsParent)
          return false
        }
        locationInputs.push(input)
        continue
      }

      const { templateId, rootFields, dataFields } = splitResourceFormData(
        resourceTypeId as ResourceTypeId,
        formData,
      )

      if (resourceTypeId === ResourceTypeId.MATERIALS) {
        materialInputs.push({
          id: tempId,
          projectId,
          name: rootFields.name ?? 'New Material',
          color: '#808080',
          iconName: 'material',
          templateId: templateId ?? null,
          data: dataFields,
        })
      } else if (resourceTypeId === ResourceTypeId.OFFLOADING_EQUIPMENT) {
        // Reuse the resource-edit input builder so icon/color/parent chosen in
        // the picker's creation panel — and the derived accessible levels — are
        // persisted, not silently replaced with defaults.
        const input = buildEquipmentInput(
          null,
          projectId,
          null,
          { templateId, rootFields, dataFields },
          this.state,
        )
        // Correlation key so the mutation result maps back to the pending id.
        input.id = tempId
        equipmentInputs.push(input)
      } else {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
        return false
      }
    }

    const tempIdToRealId = new Map<string, string>()

    if (locationInputs.length) {
      const ok = await this.runResourceMutation(
        SaveLocationsDocument,
        'locations',
        locationInputs,
        'saveManyLocations',
        tempIdToRealId,
      )
      if (!ok) return false
      this.settleMaterializedResources(tempIdToRealId)
    }

    if (equipmentInputs.length) {
      const ok = await this.runResourceMutation(
        SaveEquipmentsDocument,
        'equipments',
        equipmentInputs,
        'saveManyOffloadingEquipments',
        tempIdToRealId,
      )
      if (!ok) return false
      this.settleMaterializedResources(tempIdToRealId)
    }

    if (materialInputs.length) {
      const ok = await this.runResourceMutation(
        SaveMaterialsDocument,
        'materials',
        materialInputs,
        'saveManyMaterials',
        tempIdToRealId,
      )
      if (!ok) return false
    }

    this.settleMaterializedResources(tempIdToRealId)
    runInAction(() => this.pendingResources.clear())

    return true
  }

  /**
   * Hands each saved resource's real id to the permit fields and drops it from the queue as
   * soon as its own save succeeds. A retry after a later save fails would otherwise resend it
   * as an update, which the backend refuses for a member when the resource has no owners.
   */
  private settleMaterializedResources(tempIdToRealId: Map<string, string>) {
    this.replacePendingResourceIds(tempIdToRealId)

    runInAction(() => {
      tempIdToRealId.forEach((id, tempId) => {
        const pending = this.pendingResources.get(tempId)
        if (!pending) return
        if (pending.resourceTypeId === ResourceTypeId.LOCATIONS) {
          this.createdLocationDisplayById.set(
            id,
            toLocationDisplayFallback(pending),
          )
        }
        this.pendingResources.delete(tempId)
      })
    })
  }

  // Each input carries its pending id as the correlation key.
  private async runResourceMutation(
    mutation: any,
    variablesKey: 'locations' | 'equipments' | 'materials',
    inputs: ReadonlyArray<{ id?: unknown }>,
    resultKey:
      | 'saveManyOffloadingEquipments'
      | 'saveManyMaterials'
      | 'saveManyLocations',
    tempIdToRealId: Map<string, string>,
  ): Promise<boolean> {
    const tempIds = inputs.map(input => String(input.id))
    const result = await this.graphExecutorStore.mutate(mutation, {
      [variablesKey]: inputs,
    })

    if (result.error) {
      toast.error(Localization.translator.failedToSaveChangesTryAgain)
      return false
    }

    const savedItems: Array<{ id?: any | null } | null> =
      result.data?.[resultKey] ?? []
    const tempIdSet = new Set(tempIds)

    // Primary: match by correlation key (backend echoes client-provided ID)
    savedItems.forEach(item => {
      const returnedId = item?.id ? String(item.id) : null
      if (returnedId && tempIdSet.has(returnedId)) {
        tempIdToRealId.set(returnedId, returnedId)
      }
    })

    // Fallback: index mapping only when lengths match exactly
    const matchedCount = tempIds.filter(id => tempIdToRealId.has(id)).length
    if (matchedCount === 0 && savedItems.length === tempIds.length) {
      savedItems.forEach((item, i) => {
        if (item?.id && tempIds[i]) {
          tempIdToRealId.set(tempIds[i], String(item.id))
        }
      })
    }

    // Abort if any referenced pending resources couldn't be mapped
    const finalMatched = tempIds.filter(id => tempIdToRealId.has(id)).length
    if (finalMatched !== tempIds.length) {
      toast.error(Localization.translator.failedToSaveChangesTryAgain)
      return false
    }

    return true
  }

  @action.bound
  private replacePendingResourceIds(idMap: Map<string, string>) {
    for (const {
      types,
      accessor,
    } of SitePermitCreationFormStore.RESOURCE_FIELD_CONFIGS) {
      const fields = types.flatMap(t =>
        this.editablePermit.getAllFieldsByType(t),
      )
      fields.forEach(field => {
        field.values.forEach((value: any, index: number) => {
          if (Array.isArray(value)) {
            field.values[index] = value.map((v: any) =>
              v?.[accessor] && idMap.has(v[accessor])
                ? { ...v, [accessor]: idMap.get(v[accessor]) }
                : v,
            )
          } else if (value?.[accessor] && idMap.has(value[accessor])) {
            field.values[index] = {
              ...value,
              [accessor]: idMap.get(value[accessor]),
            }
          }
        })
      })
    }
  }

  public createPermit = async (
    closeForm: () => void,
    isDraft: boolean = false,
  ) => {
    // doesnt handle linked workflows yet
    if (this.shouldSkipClosureValidationOnce) {
      this.shouldSkipClosureValidationOnce = false
    } else {
      const isValid = await this.validateClosuresForEntity(
        this.editablePermit,
        this.workflowFieldsStore,
        this.workflowFieldsStore.recurringSettings,
      )
      if (!isValid) {
        // For recurring workflows, allow user to continue; remember intent
        if (this.workflowFieldsStore.recurringSettings) {
          this.pendingCreateArgs = { closeForm, isDraft }
        }
        return
      }
    }

    if (!(await this.runLocationsValidationNow())) {
      return
    }

    if (!isDraft && !(await this.askToConfirmHoursWarnings('submit'))) {
      return
    }

    const resourcesCreated = await this.materializePendingResources()
    if (!resourcesCreated) return

    // Last point the map's editor is mounted: closeForm() below unmounts it before the flush.
    this.markupsStore.commitPending(this.markupDraftId)
    // Committed before the capture await: closing the form during the capture
    // window must not discard the markup buffer — the create below always
    // dispatches once past this point, and its success callback flushes it.
    this.markupBufferHandedOffForCreate = true
    let permitsToSave: Workflow[]
    try {
      await this.captureLocationScreenshots()
      permitsToSave = this.prepareForCreate(isDraft)
    } catch (error) {
      // Any abort before the dispatch means nothing will flush the buffer —
      // teardown must be allowed to discard it again.
      this.markupBufferHandedOffForCreate = false
      throw error
    }

    if (this.workflowFieldsStore.recurringSettings) {
      this.createRecurringPermit(permitsToSave, closeForm)
    } else {
      this.eventsStore.dispatch(
        e.SAVE_SITE_PERMITS,
        permitsToSave,
        null,
        this.handlePermitCreateSuccess,
        this.handleCreateError,
      )

      this.markupBufferHandedOffForCreate = true
      closeForm()
    }
  }

  // Who may act is decided from existingPermit (the server copy), but a save
  // advances editablePermit first. Until the saved step comes back, another
  // forward action would complete the step after the one the user was cleared for.
  @computed
  public get isStepAdvancePending(): boolean {
    return (
      this.isUpdateRequested ||
      (!!this.existingPermit &&
        this.editablePermit?.currentWorkflowStepId !==
          this.existingPermit.currentWorkflowStepId)
    )
  }

  @action.bound
  private setUpdateRequested(isRequested: boolean) {
    this.isUpdateRequested = isRequested
  }

  // Taken before prepareForUpdate. A failed save would otherwise leave
  // editablePermit on the step it advanced to and without the current step's
  // fields (moved into stepSubmissions). Undo both so a retry completes the same
  // step and re-submits the same values instead of an empty submission.
  private getFailedSaveRollback(): () => void {
    const permit = this.editablePermit
    const { fields } = permit
    const { currentWorkflowStepId, workflowStepLevel } = this.existingPermit

    return () =>
      runInAction(() => {
        permit.setWorkflowStepId(currentWorkflowStepId)
        permit.setWorkflowStepLevel(workflowStepLevel)
        permit.fields = fields
      })
  }

  public updatePermit = async (
    message?: string,
    closeForm?: () => void,
    skipStepAdvancement: boolean = false,
  ) => {
    if (this.isUpdateRequested) {
      return
    }
    this.setUpdateRequested(true)

    try {
      await this.validateAndSaveUpdate(message, closeForm, skipStepAdvancement)
    } finally {
      this.setUpdateRequested(false)
    }
  }

  private validateAndSaveUpdate = async (
    message: string,
    closeForm: () => void,
    skipStepAdvancement: boolean,
  ) => {
    // doesnt handle linked workflows yet
    const isValid = await this.validateClosuresForEntity(
      this.editablePermit,
      this.workflowFieldsStore,
      this.isRecurringSettingsChanged
        ? this.workflowFieldsStore.recurringSettings
        : undefined,
    )

    if (!isValid) {
      return
    }

    if (!(await this.runLocationsValidationNow())) {
      return
    }

    const resourcesCreated = await this.materializePendingResources()
    if (!resourcesCreated) return

    await this.captureLocationScreenshots()

    if (this.isRecurringSettingsChanged) {
      const permitsToSave = this.prepareForCreate()
      this.createRecurringPermit(permitsToSave, closeForm)
      this.deleteForm(true)
      return
    }

    const rollbackFailedSave = this.getFailedSaveRollback()
    const permitsToSave = this.prepareForUpdate(skipStepAdvancement)

    const finalMessage = this.editablePermit.isChanged
      ? this.getChangedFieldsMessage(message || '')
      : null

    this.eventsStore.dispatch(
      e.SAVE_SITE_PERMITS,
      permitsToSave,
      finalMessage,
      this.resetRecurringSettingsIfNeed,
      rollbackFailedSave,
    )

    closeForm?.()
  }

  private getChangedFieldsMessage = (userNoteMessage: string): string => {
    const {
      areRequiredFieldsChanged,
      availableWorkflowSteps,
      firstTwoStepsFieldIds,
      fields,
    } = this.workflowFieldsStore

    const isOnlyFromTwoSteps =
      areRequiredFieldsChanged && availableWorkflowSteps.length > 2
    const changedFields = fields.filter(
      f =>
        f.isChanged && (!isOnlyFromTwoSteps || firstTwoStepsFieldIds.has(f.id)),
    )

    changedFields.forEach(field => {
      const changeMessage = this.getFieldChangeMessage(field)
      if (changeMessage) {
        userNoteMessage += `${changeMessage}\n`
      }
    })

    userNoteMessage += FIELDS_AND_NOTE_SEPARATOR
    return userNoteMessage || EMPTY_STRING
  }

  private getFieldChangeMessage(field: IPermitFormField): string {
    const fieldName = getFieldName(field)

    if (field.type === SpecificPermitFieldTypes.Title) {
      return this.getTitleChangeMessage(fieldName)
    }

    if (field.type === PermitFieldType.StartFinishDates) {
      return this.getDateRangeChangeMessage(fieldName)
    }

    if (field.type === SpecificPermitFieldTypes.SpecificInspectionOptions) {
      return this.changedFieldsMessageFormatter.getInspectionOptionsMessage(
        this.existingPermit?.specificInspectionOptions,
        this.editablePermit?.specificInspectionOptions,
        field,
      )
    }

    return this.getStandardFieldChangeMessage(field, fieldName)
  }

  private getTitleChangeMessage(fieldName: string): string {
    const oldValue = this.existingPermit?.title || DASH
    const newValue = this.editablePermit?.title || DASH
    return `${fieldName}: ${oldValue} -> ${newValue}`
  }

  private getDateRangeChangeMessage(fieldName: string): string {
    const oldValue =
      this.existingPermit.getDateAndTimeToDisplay(this.projectDateStore) || DASH
    const newValue =
      this.editablePermit.getDateAndTimeToDisplay(this.projectDateStore) || DASH
    return `${fieldName}: ${oldValue} -> ${newValue}`
  }

  private getStandardFieldChangeMessage(
    field: IPermitFormField,
    fieldName: string,
  ): string {
    const oldValue = this.changedFieldsMessageFormatter.toDisplayString(
      this.getFieldValue(field, true),
      field,
    )
    const newValue = this.changedFieldsMessageFormatter.toDisplayString(
      this.getFieldValue(field, false),
      field,
    )

    return `${fieldName}: ${oldValue} -> ${newValue}`
  }

  private getFieldValue(field: IPermitFormField, isExisting: boolean): any {
    return this.workflowFieldsStore.getFieldValues<any>(
      field.id,
      field.tableId,
      null,
      isExisting,
      field.parentInstanceId,
      field.parentValueIndex,
    )
  }

  private extractChildWorkflows(
    isDraft: boolean = false,
    linkedWorkflowFields?: PermitField[],
  ): Workflow[] {
    const workflowFields =
      linkedWorkflowFields ??
      this.editablePermit.fields.flatMap(f =>
        f.type === PermitFieldType.Table
          ? (f.values.flat() as PermitField[]).filter(sub =>
              isLinkedWorkflowLikeField(sub.type),
            )
          : isLinkedWorkflowLikeField(f.type)
            ? [f]
            : [],
      )

    const childPermits: Workflow[] = []
    let linkedIdIndex = 0

    workflowFields.forEach(field => {
      field.values.forEach((value: string | Workflow, index: number) => {
        if (typeof value === 'string') return

        const childPermitId =
          linkedIdIndex < this.linkedWorkflowsIds.length
            ? this.linkedWorkflowsIds[linkedIdIndex++]
            : BsonObjectId.generate()

        const newWorkflowStore = new SitePermitCreationFormStore(
          this.eventsStore,
          this.projectDateStore,
          this.fileUploadingStore,
          this.permitTypesStore,
          this.userProjectsStore,
          this.projectMembersStore,
          this.companiesStore,
          this.locationAttributesStore,
          this.materialsStore,
          this.materialCategoryStore,
          this.tagsStore,
          this.graphExecutorStore,
          this.closuresStore,
          this.markupsStore,
          this.sitePermitsStore,
          this.permitStatusChangesStore,
          this.permitInspectionsStore,
          this.permitInspectionChangesStore,
          this.calendarPlaceholderStore,
          this.recurringWorkflowSettingsStore,
          this.sitemapItemsStore,
        )

        const sliceLength = this.linkedWorkflowsIds.length - linkedIdIndex
        newWorkflowStore.linkedWorkflowsIds =
          this.linkedWorkflowsIds.slice(linkedIdIndex)

        newWorkflowStore.workflowFieldsStore.setDuplicateWorkflowData(value)

        newWorkflowStore.editablePermit.id = childPermitId
        const permitsToSave = newWorkflowStore.prepareForCreate(isDraft)

        // Advance past any descendant IDs consumed recursively by
        // the child's own extractChildWorkflows so the parent's
        // final splice removes the full range of consumed IDs.
        const descendantsConsumed =
          sliceLength - newWorkflowStore.linkedWorkflowsIds.length
        linkedIdIndex += descendantsConsumed

        field.values[index] = childPermitId

        childPermits.push(...permitsToSave)
      })
    })

    // Remove consumed IDs so they aren't reused by a subsequent
    // extraction (e.g. a second inspection on the same form session).
    if (linkedIdIndex > 0) {
      this.linkedWorkflowsIds.splice(0, linkedIdIndex)
    }

    return childPermits
  }

  @action.bound
  public addToUploadLogMap(workflowId: string, uploadLog: IUploadLog) {
    this.pendingUploadLogs.push({
      workflowId,
      uploadLog,
    })
  }

  public addInspection = async () => {
    const { editableEntity, fields, deselectInspection } =
      this.inspectionFieldsStore

    runInAction(() => {
      editableEntity.fields = editableEntity.fields.filter(f =>
        fields.some(({ id }) => id === f.fieldId),
      )
    })

    const isValid = await this.validateClosuresForEntity(
      this.editablePermit,
      this.inspectionFieldsStore,
    )
    if (!isValid) {
      return
    }

    this.dispatchInspectionWithChildren(
      editableEntity,
      e.ADD_SITE_PERMIT_INSPECTION,
      deselectInspection,
    )
  }

  public updateInspection = async () => {
    const { editableEntity, fields, deselectInspection } =
      this.inspectionFieldsStore

    runInAction(() => {
      editableEntity.fields = editableEntity.fields.filter(f =>
        fields.some(({ id }) => id === f.fieldId),
      )
    })

    const isValid = await this.validateClosuresForEntity(
      this.editablePermit,
      this.inspectionFieldsStore,
    )
    if (!isValid) {
      return
    }

    this.dispatchInspectionWithChildren(
      editableEntity,
      e.UPDATE_SITE_PERMIT_INSPECTION,
      deselectInspection,
    )
  }

  private dispatchInspectionWithChildren(
    inspection: PermitInspection,
    event:
      | typeof e.ADD_SITE_PERMIT_INSPECTION
      | typeof e.UPDATE_SITE_PERMIT_INSPECTION,
    onComplete: () => void,
  ): void {
    const linkedWorkflowFields = inspection.fields.flatMap(f =>
      f.type === PermitFieldType.Table
        ? (f.values.flat() as PermitField[]).filter(sub =>
            isLinkedWorkflowLikeField(sub.type),
          )
        : isLinkedWorkflowLikeField(f.type)
          ? [f]
          : [],
    )

    // Snapshot all mutable state before extractChildWorkflows mutates it.
    // If either save fails we restore everything for a clean retry.
    const fieldSnapshots = linkedWorkflowFields.map(f => [...f.values])
    const reservedIdsSnapshot = [...this.linkedWorkflowsIds]
    const childPermits = this.extractChildWorkflows(false, linkedWorkflowFields)

    if (childPermits.length) {
      const uploadLogsSnapshot = [...this.pendingUploadLogs]

      const restoreOnFailure = () => {
        runInAction(() => {
          linkedWorkflowFields.forEach((f, i) => {
            f.values = fieldSnapshots[i]
          })
          this.linkedWorkflowsIds = reservedIdsSnapshot
          this.pendingUploadLogs = uploadLogsSnapshot
        })
      }

      const cleanupOrphansAndRestore = () => {
        const childIds = childPermits.map(c => c.id).filter(Boolean)
        if (childIds.length) {
          this.graphExecutorStore
            .mutate(DeleteFormsDocument, { ids: childIds })
            .then(() => restoreOnFailure())
            .catch(() => {
              toast.error(Localization.translator.failedToSaveChangesTryAgain)
            })
        } else {
          restoreOnFailure()
        }
      }

      this.eventsStore.dispatch(
        e.SAVE_SITE_PERMITS,
        childPermits,
        null,
        (savedPermits: ISitePermit[]) => {
          // Defer upload log reconciliation until the inspection mutation
          // succeeds — running it here would race with cleanupOrphansAndRestore
          // if the inspection save fails.
          this.eventsStore.dispatch(
            event,
            inspection,
            () => {
              if (savedPermits?.length && this.pendingUploadLogs.length > 0) {
                this.updatePendingUploadLogs(savedPermits)
              }
              onComplete()
            },
            cleanupOrphansAndRestore,
          )
        },
        restoreOnFailure,
      )
    } else {
      this.eventsStore.dispatch(event, inspection, onComplete)
    }
  }

  @action.bound
  public addNotInUseInspection() {
    const { emptyInspection, deselectInspection } = this.inspectionFieldsStore
    this.eventsStore.dispatch(e.ADD_SITE_PERMIT_INSPECTION, emptyInspection)
    deselectInspection()
  }

  @action.bound
  public async deleteForm(
    thisAndFutureRecurring: boolean = false,
    closeForm?: () => void,
  ) {
    this.isDeleteConfirmModalOpen = false

    const isDeleted = await this.sitePermitsStore.deleteForms(
      [this.existingPermit],
      thisAndFutureRecurring,
    )

    // A failed delete keeps the form open behind its error toast.
    if (isDeleted) {
      runInAction(() => closeForm?.())
    }
  }

  @action.bound
  public openDeadlineModalIfNeed() {
    this.isDeadlineModalShown =
      !this.isAdminOrFormsMaster &&
      this.isInsideDeadline &&
      !this.isMarkedAsLateRequest
  }

  @action.bound
  public closeDeadlineModal() {
    this.isDeadlineModalShown = false
  }

  // Hours of operation never block, but the person submitting or approving sees
  // every note once, together, right before the action — not only under fields
  // they may have scrolled past. Resolves whether they went ahead; callers ask
  // before changing anything, so going back leaves the workflow as it was.
  public askToConfirmHoursWarnings(
    action: 'submit' | 'approve',
  ): Promise<boolean> {
    if (!this.hoursOfOperationWarnings.length && !this.projectHoursNote) {
      return Promise.resolve(true)
    }

    return new Promise(resolve => {
      runInAction(() => {
        this.resolveHoursWarningDialog = resolve
        this.hoursWarningDialogAction = action
      })
    })
  }

  @action.bound
  public confirmHoursWarnings() {
    this.closeHoursWarningDialog(true)
  }

  @action.bound
  public cancelHoursWarnings() {
    this.closeHoursWarningDialog(false)
  }

  private closeHoursWarningDialog(isConfirmed: boolean) {
    const resolve = this.resolveHoursWarningDialog
    this.resolveHoursWarningDialog = null
    this.hoursWarningDialogAction = null
    resolve?.(isConfirmed)
  }

  @action.bound
  public closeClosuresConflictModal() {
    this.isClosuresConflictModalShown = false
  }

  @action.bound
  public continueDespiteClosuresConflict() {
    if (this.pendingPickerApply) {
      this.isClosuresConflictModalShown = false
      const apply = this.pendingPickerApply
      this.pendingPickerApply = null
      apply()
      return
    }

    this.isClosuresConflictModalShown = false
    this.shouldSkipClosureValidationOnce = true
    const args = this.pendingCreateArgs
    this.pendingCreateArgs = null
    if (args) {
      this.createPermit(args.closeForm, args.isDraft)
    }
  }

  public async validateClosuresForCurrentForPicker(
    onContinueApply?: () => void,
  ): Promise<boolean> {
    const recurringSettings = this.workflowFieldsStore.recurringSettings

    const isValid =
      await this.locationClosureValidator.validateClosuresForEntity(
        this.editablePermit,
        this.workflowFieldsStore,
        this.template?.type,
        recurringSettings,
      )

    if (recurringSettings && !isValid) {
      runInAction(() => {
        this.pendingPickerApply = onContinueApply || null
        this.isClosuresConflictModalShown = true
      })
      return false
    }

    if (!recurringSettings && !isValid) {
      runInAction(() => {
        this.pendingPickerApply = null
        this.isClosuresConflictModalShown = true
      })
      return false
    }

    return true
  }

  private validateClosuresForEntity = async (
    permit: Workflow,
    fieldsStore: PermitFieldsBaseStore,
    recurringSettings?: RecurringWorkflowSettings,
  ): Promise<boolean> => {
    const isValid =
      await this.locationClosureValidator.validateClosuresForEntity(
        permit,
        fieldsStore,
        this.template?.type,
        recurringSettings,
      )

    if (!isValid) {
      runInAction(() => {
        this.isClosuresConflictModalShown = true
      })
    }

    return isValid
  }

  @action.bound
  private openNonWorkTimeModalIfNeed() {
    this.isNonWorkTimesModalOpened =
      this.shouldBlockOnNonWorkTimes &&
      !this.shouldShowNonWorkLabel &&
      !this.isPermitInsideWorkTime &&
      !this.isAdminOrFormsMaster
  }

  @action.bound
  public closeNonWorkTimeModal() {
    this.isNonWorkTimesModalOpened = false
  }

  @action.bound
  public markPermitAsNonWorkTimeIfNeed() {
    if (this.editablePermit?.id && !this.isPermitInsideWorkTime) {
      return this.shouldBlockOnNonWorkTimes && !this.isAdminOrFormsMaster
        ? this.blockSubmitAndShowNonWorkLabel()
        : this.showNonWorkTimeLabel()
    }

    this.openNonWorkTimeModalIfNeed()
  }

  @action.bound
  private setAsLateRequest() {
    this.editablePermit.isLateRequest = true
  }

  @action.bound
  private unsetAsLateRequest() {
    this.editablePermit.isLateRequest = false
  }

  @action.bound
  public showNonWorkTimeLabel() {
    this.shouldShowNonWorkLabel = true
  }

  @action.bound
  public hideNonWorkTimeLabel() {
    this.shouldShowNonWorkLabel = false
  }

  @action.bound
  public blockSubmitButton() {
    this.shouldBlockSubmitButton = true
  }

  @action.bound
  public unblockSubmitButton() {
    this.shouldBlockSubmitButton = false
  }

  @action.bound
  public blockSubmitAndShowNonWorkLabel() {
    this.blockSubmitButton()
    this.showNonWorkTimeLabel()
  }

  public setRestrictionsAndBlockSubmitIfNeed = () => {
    if (this.areNonWorkAndDeadlineModalsShown) {
      this.closeDeadlineModal()
      this.setAsLateRequest()

      if (this.shouldBlockLateRequesting) {
        this.blockSubmitButton()
      }
    }

    this.closeNonWorkTimeModal()
    this.showNonWorkTimeLabel()

    if (this.shouldBlockOnNonWorkTimes) {
      this.blockSubmitButton()
    }
  }

  @action.bound
  public showFormTypeSelector() {
    this.isTypeSelectorShown = true
    this.selectedFieldDropdownType = FormDropdownType.FORM_TYPE
  }

  @action.bound
  public hideFormTypeSelector() {
    this.isTypeSelectorShown = false
    this.selectedFieldDropdownType = FormDropdownType.NONE
  }

  /** resetSelectedField alone leaves isTypeSelectorShown set under the next picker. */
  @action.bound
  public closePicker() {
    this.resetSelectedField()
    this.hideFormTypeSelector()
  }

  @action.bound
  public showSignatureModal(
    typeField: IPermitTypeField,
    fieldsStore: IPermitFieldsStore,
    index: number,
    initialValue: ISignature,
    parentInstanceId?: string,
    parentValueIndex?: number,
    tableId?: string,
    tableRowIndex?: number,
  ) {
    this.selectedField = typeField
    this.signatureFieldsStore = fieldsStore
    this.signatureEntity = fieldsStore.editableEntity
    this.selectedFieldIndex = index
    this.selectedParentInstanceId = parentInstanceId
    this.selectedParentValueIndex = parentValueIndex
    this.selectedTableId = tableId
    this.selectedTableRowIndex = tableRowIndex
    this.signatureInitialValue = initialValue
    this.isSignatureModalShown = true
  }

  @action.bound
  public hideSignatureModal() {
    this.isSignatureModalShown = false
    this.signatureInitialValue = null
    this.signatureFieldsStore = null
    this.signatureEntity = null
    this.resetSelectedField()
  }

  @action.bound
  public confirmSignature(signature: ISignature) {
    const isSignedEntityOpen =
      !!this.signatureEntity &&
      this.signatureFieldsStore.editableEntity === this.signatureEntity
    if (this.selectedField && isSignedEntityOpen) {
      if (this.selectedTableId) {
        this.signatureFieldsStore.changeTableFieldValue(
          this.selectedTableId,
          this.selectedTableRowIndex,
          this.selectedField,
          this.selectedFieldIndex,
          signature,
          this.selectedParentInstanceId,
          this.selectedParentValueIndex,
        )
      } else {
        this.signatureFieldsStore.changeFieldValue(
          this.selectedField,
          this.selectedFieldIndex,
          signature,
          this.selectedParentInstanceId,
          this.selectedParentValueIndex,
        )
      }
    }
    this.hideSignatureModal()
  }

  public onPermitUpdated = (eventContext: EventContext) => {
    const [eventType] = eventContext.event

    if (updateSuccessEvents.includes(eventType)) {
      Promise.resolve().then(() => this.scrollToBottom())
    }
  }

  private get state() {
    return this.eventsStore.appState
  }

  @action.bound
  public deselectUsersIfNeeded() {
    const selectedCompanies = this.editablePermit.companyIds
      .map(id => this.companiesStore.getCompanyById(id)?.id)
      .filter(id => id)

    const responsibleFields = this.editablePermit.fields?.filter(
      f => f.type === PermitFieldType.Assignee,
    )

    responsibleFields.forEach(field => {
      field.values = field.values.filter(userId => {
        const user = this.projectMembersStore.getById(userId)
        return (
          !user ||
          selectedCompanies.includes(
            UserProject.getCompanyId(user, this.userProjectsStore),
          )
        )
      })
    })
  }

  @computed
  public get isAttachmentUploading(): boolean {
    return this.attachmentUploadingCount > 0
  }

  @action.bound
  private setAttachmentUploading() {
    this.attachmentUploadingCount++
  }

  @action.bound
  private resetAttachmentUploading() {
    this.attachmentUploadingCount = Math.max(
      0,
      this.attachmentUploadingCount - 1,
    )
  }

  public get isResponsibleShown(): boolean {
    return this.template?.initialStep?.fields?.find(
      f => f.type === PermitFieldType.Assignee,
    )?.isShown
  }

  @computed
  public get existingPermit(): Workflow {
    return this.workflowFieldsStore.existingEntity
  }

  private get isPermitInsideWorkTime(): boolean {
    if (!this.editablePermit) {
      return true
    }

    const { startDate, endDate } = this.editablePermit

    if (!startDate || !endDate) {
      return true
    }

    // Whole-interval, matching the dates field — otherwise the confirm dialog
    // and the banner would disagree with the message under the field.
    return this.projectDateStore.isIntervalInsideWorkingHours(
      startDate,
      endDate,
    )
  }

  @action.bound
  private createInspectionOptionsIfNotExists() {
    if (!this.editablePermit.specificInspectionOptions) {
      this.editablePermit.specificInspectionOptions = copyObject(
        this.inspectionOptions,
      )
    }
  }

  @action.bound
  private setWorkflowStepLevel() {
    if (this.editablePermit.isDraft) {
      return
    }

    if (this.workflowFieldsStore.areRequiredFieldsChanged) {
      return this.editablePermit.setWorkflowStepLevel(
        getWorkflowStepLevel(
          this.template.initialStep.id,
          this.template.stepsWithoutRecurring,
        ),
      )
    }
    if (this.workflowFieldsStore.areOptionalFieldsChanged) {
      return this.editablePermit.setWorkflowStepLevel(
        this.existingPermit.workflowStepLevel,
      )
    }

    const steps = this.template.stepsWithoutRecurring
    const isTerminalOnLastStep =
      this.editablePermit.isDeniedOrFailed &&
      this.editablePermit.currentWorkflowStepId === steps[steps.length - 1]?.id

    this.editablePermit.setWorkflowStepLevel(
      isTerminalOnLastStep
        ? 0
        : getWorkflowStepLevel(
            this.editablePermit.currentWorkflowStepId,
            steps,
          ),
    )
  }

  @action.bound
  private setNextWorkflowStep() {
    if (this.editablePermit.isTerminal || this.editablePermit.isDraft) {
      return
    }

    if (this.workflowFieldsStore.areRequiredFieldsChanged) {
      this.editablePermit.setWorkflowStepId(this.template.initialStep.id)
    } else if (this.workflowFieldsStore.areOptionalFieldsChanged) {
      this.editablePermit.setWorkflowStepId(
        this.existingPermit.currentWorkflowStepId,
      )
      return
    }

    advanceToNextWorkflowStep(
      this.editablePermit,
      this.template.stepsWithoutRecurring,
    )
  }

  public get selectedPermitCompanyIds(): string[] {
    return this.editablePermit?.companyIds || []
  }

  @computed
  public get isAnyDialogOpened(): boolean {
    return (
      !!this.selectedField ||
      this.isNonWorkTimesModalOpened ||
      this.isDeadlineModalShown
    )
  }

  public get isAdminOrFormsMaster(): boolean {
    return this.state.userActiveProjectSettings.isAdminOrFormsMaster
  }

  // Whether the workflow's status may be changed right now. Shared by the more
  // menu's entry, the ActionBar's caret and the selector it opens, so the three
  // cannot drift apart. Unsaved field edits must be committed first.
  @computed
  public get canChangeWorkflowStatus(): boolean {
    const { areRequiredFieldsChanged, areOptionalFieldsChanged } =
      this.workflowFieldsStore

    return (
      this.isAdminOrFormsMaster &&
      !areRequiredFieldsChanged &&
      !areOptionalFieldsChanged
    )
  }

  @computed
  public get isWorkflowCreator(): boolean {
    return this.existingPermit?.isCreator(this.state.user.id)
  }

  @computed
  public get inspectionMaxNumber(): number {
    const { specificInspectionOptions } = this.editablePermit

    switch (specificInspectionOptions?.inspectionFrequencyType) {
      case CalendricalType.Day:
        return MAX_DAYS_VALUE
      case CalendricalType.Week:
        return MAX_WEEKS_VALUE
      case CalendricalType.Month:
        return MAX_MONTHS_VALUE
    }
  }

  public get isWorkflowCompanySelected(): boolean {
    return this.selectedField?.type === PermitFieldType.Company
  }

  public get isDatePickerDisplayed(): boolean {
    return (
      this.selectedField?.type === PermitFieldType.StartFinishDates ||
      this.selectedField?.type === PermitFieldType.AdditionalDate
    )
  }

  @computed
  public get isEquipmentPickerDisplayed(): boolean {
    return equipmentPermitFieldTypes.includes(
      this.selectedSubField?.type || this.selectedField?.type,
    )
  }

  @computed
  public get isMaterialResourcePickerDisplayed(): boolean {
    return materialResourcePermitFieldTypes.includes(
      this.selectedSubField?.type || this.selectedField?.type,
    )
  }

  @computed
  public get isLocationPickerDisplayed(): boolean {
    if (this.isEquipmentPickerDisplayed) return false
    if (this.isMaterialResourcePickerDisplayed) return false
    return locationPermitFieldTypes.includes(
      this.selectedSubField?.type || this.selectedField?.type,
    )
  }

  public get isCompanyPickerDisplayed(): boolean {
    return (
      this.selectedField?.type === PermitFieldType.CompanyInformational ||
      this.isWorkflowCompanySelected
    )
  }

  public get isRequesterPickerDisplayed(): boolean {
    return this.selectedField?.type === PermitFieldType.Requester
  }

  public get isResponsiblePickerDisplayed(): boolean {
    return this.selectedField?.type === PermitFieldType.Assignee
  }

  public get isSubscriberPickerDisplayed(): boolean {
    return this.selectedField?.type === PermitFieldType.Subscriber
  }

  public get isInfoUserPickerDisplayed(): boolean {
    return this.selectedField?.type === PermitFieldType.User
  }

  public get isWorkflowPickerDisplayed(): boolean {
    return isLinkedWorkflowLikeField(this.selectedField?.type)
  }

  public get isUserPickerDisplayed(): boolean {
    return (
      this.isRequesterPickerDisplayed ||
      this.isResponsiblePickerDisplayed ||
      this.isSubscriberPickerDisplayed ||
      this.isInfoUserPickerDisplayed
    )
  }

  public get datePickerStart(): number {
    const { startDate } = this.editablePermit

    if (this.isPermitDatesFieldSelected) {
      return startDate
    }

    return this.selectedFieldValues[this.selectedFieldIndex] || Date.now()
  }

  public get datePickerEnd(): number {
    return this.isPermitDatesFieldSelected
      ? this.editablePermit.endDate
      : this.datePickerStart
  }

  public get isAllDay(): boolean {
    return this.editablePermit.isAllDay
  }

  @action.bound
  public toggleAllDay() {
    const { startDate: editableStartDate, endDate: editableEndDate } =
      this.editablePermit

    const { getAllDayWindowForDate } = this.projectDateStore

    const newIsAllDay = !this.editablePermit.isAllDay

    if (!newIsAllDay) {
      this.applyDates(
        new Date(editableStartDate),
        new Date(editableEndDate),
        newIsAllDay,
      )
      return
    }

    // What the workflow's own start date works, rather than the project-wide
    // window it may share nothing with — already carried onto the next date when
    // the shift runs past midnight (STX1-983).
    const { startTime, endTime } = getAllDayWindowForDate(editableStartDate)

    this.applyDates(startTime, endTime, newIsAllDay)
  }

  public get isOneDayMode(): boolean {
    return !this.isPermitDatesFieldSelected
  }

  public get maxDateRangeInYears(): number {
    return this.isPermitDatesFieldSelected ? MAX_DATE_RANGE_YEARS : 0
  }

  public get shouldShowTimePicker(): boolean {
    return this.isPermitDatesFieldSelected
  }

  public get dropdownModalOptions(): IFormDropdownOption[] {
    if (this.selectedFieldDropdownType === FormDropdownType.NONE) {
      return []
    }

    const options = this.dropdownOptionsByType
    if (!this.isTypeSelectorShown) {
      options.unshift({ title: NO_VALUE, value: EMPTY_STRING })
    }

    return options.filter(opt => {
      const searchKey = this.modalSearchValue?.toLowerCase() || EMPTY_STRING
      return (
        opt.title?.toLowerCase()?.includes(searchKey) ||
        opt.cardOptions?.some(
          v => v.isSearchable && v.value?.toLowerCase().includes(searchKey),
        )
      )
    })
  }

  private get dropdownOptionsByType(): IFormDropdownOption[] {
    switch (this.selectedFieldDropdownType) {
      case FormDropdownType.DEFAULT:
        return (
          this.selectedField?.selectOptions?.map(v => ({
            title: v,
            value: v,
          })) || []
        )
      case FormDropdownType.MATERIAL:
        return this.dropdownMaterialOptions
      case FormDropdownType.PROCUREMENT_ID:
        return this.dropdownProcurementOptions
      case FormDropdownType.FORM_TYPE:
        return this.permitTypes.map(type => ({
          title: type.name,
          value: type.id,
          icon: PermitTypeIcon({
            iconName: type.iconName,
            className: 'row mr10 no-grow',
          }),
        }))

      default:
        return []
    }
  }

  private get dropdownMaterialOptions(): IFormDropdownOption[] {
    return this.materialsStore.list
      .reduce((list, material) => {
        const category = this.materialCategoryStore.getInstanceById(
          material.categoryId,
        )

        if (!category?.name || !material.productName) {
          return list
        }

        list.push({
          title: material.productName,
          value: material.id,
          cardOptions: [
            {
              title: this.selectedField.caption + ' L1',
              value: category.name,
              isSearchable: true,
            },
            {
              title: this.selectedField.caption + ' L2',
              value: material.productName,
              hasBoldFont: true,
              isSearchable: true,
            },
          ],
        })
        return list
      }, [] as IFormDropdownOption[])
      .sort((a, b) => {
        const categoryCompare = this.collator.compare(
          a.cardOptions[0].value,
          b.cardOptions[0].value,
        )
        return categoryCompare !== 0
          ? categoryCompare
          : this.collator.compare(a.title, b.title)
      })
  }

  private get dropdownProcurementOptions(): IFormDropdownOption[] {
    const { list, getInstanceById: getMaterialById } = this.materialsStore

    const formMaterial = this.selectedFieldValues[
      this.selectedFieldIndex
    ] as IFormMaterial

    const material = getMaterialById(formMaterial?.materialId)

    return list
      .reduce((res, m) => {
        if (material?.hasProcurementIds && m.id !== material.id) {
          return res
        }

        m.procurementDataList?.forEach(pr =>
          res.push({
            value: pr.procurementId,
            title: pr.procurementId,
            cardOptions: this.getProcurementCardOptions(pr, m),
          }),
        )
        return res
      }, [] as IFormDropdownOption[])
      .sort((a, b) => this.collator.compare(a.title, b.title))
  }

  private getProcurementCardOptions = (
    procurementData: IMaterialProcurementData,
    material: Material,
  ): IFormDropdownOptionCard[] => {
    const installLocation = this.locationAttributesStore.getById(
      procurementData.installLocationId,
    )
    const deliveryLocation = this.locationAttributesStore.getById(
      procurementData.deliveryLocationId,
    )

    return [
      {
        title: Localization.translator.procurementID,
        value: procurementData.procurementId,
        hasBoldFont: true,
      },
      {
        title: this.selectedField.caption + ' L1',
        value: this.materialCategoryStore.getCategoryNameById(
          material.categoryId,
        ),
      },
      {
        title: this.selectedField.caption + ' L2',
        value: material.productName,
      },
      {
        title: Localization.translator.description,
        value: procurementData.description,
      },
      {
        title: Localization.translator.plannedInstallLocation,
        value: installLocation?.name,
        location: installLocation,
        isSearchable: true,
      },
      {
        title: Localization.translator.plannedDeliveryLocation,
        value: deliveryLocation?.name,
        location: deliveryLocation,
      },
      {
        title: Localization.translator.plannedQuantity,
        value: procurementData.plannedQuantity?.toString(),
      },
    ]
  }

  public get formDetailTabs(): Array<ITabObject<FormDetailsTab>> {
    return [
      {
        title: Localization.translator.workflow,
        page: FormDetailsTab.FORM,
      },
      {
        title: Localization.translator.log,
        page: FormDetailsTab.LOG,
      },
    ]
  }

  @action.bound
  public switchActiveTab(newActiveTab: FormDetailsTab) {
    this.activeDetailsTab = newActiveTab

    Promise.resolve().then(() => this.scrollToBottom())
  }

  private logOwnerToken: number | null = null

  public loadFormLogData = () => {
    this.logLoadingTimeoutId = setTimeout(() => {
      this.logOwnerToken = this.permitStatusChangesStore.listenFor(
        this.existingPermit.id,
      )
    }, LOADING_LOG_DELAY_MS)
  }

  public clearFormLogData = () => {
    clearTimeout(this.logLoadingTimeoutId)
    this.logLoadingTimeoutId = null

    if (this.logOwnerToken !== null) {
      this.permitStatusChangesStore.stopListening(this.logOwnerToken)
      this.logOwnerToken = null
    }
    this.discardPendingMarkups()
  }

  public discardPendingMarkups = () => {
    this.markupsStore.discardPending(this.markupDraftId)
  }

  // Teardown hook: forget buffered markups for an ABANDONED creation. Skipped
  // once a create is dispatched — the async create-success callback flushes the
  // buffer, and the form closes synchronously before that callback runs.
  public discardPendingMarkupsIfAbandoned = () => {
    // Every teardown, submitted or not: the map must not write into a form that is gone.
    if (
      this.eventsStore?.appState?.applyHandedOffPreciseLocation ===
      this.applyPreciseLocationFromMap
    ) {
      this.eventsStore.appState.applyHandedOffPreciseLocation = null
    }
    if (this.markupBufferHandedOffForCreate) {
      return
    }
    this.discardPendingMarkups()
  }

  public get isMarkedAsLateRequest(): boolean {
    return !!this.editablePermit?.isLateRequest
  }

  public get shouldBlockLateRequesting(): boolean {
    return this.template?.shouldBlockBooking
  }

  public get bookingDeadlineInterval(): number {
    return this.template?.bookingDeadlineInterval
  }

  public get areNonWorkAndDeadlineModalsShown(): boolean {
    return this.isDeadlineModalShown && this.isNonWorkTimesModalOpened
  }

  // What the non-work-time dialog is really asking: will the request still be
  // unsubmittable once it is applied? Mirrors what
  // setRestrictionsAndBlockSubmitIfNeed decides — the deadline setting counts
  // only while its own dialog is folded into this one, on its own it gates a
  // later date rather than this one, and a block a previous apply already set
  // never lifts here.
  public get willBlockSubmitOnNonWorkTimeApply(): boolean {
    return (
      this.shouldBlockSubmitButton ||
      this.shouldBlockOnNonWorkTimes ||
      (this.areNonWorkAndDeadlineModalsShown && this.shouldBlockLateRequesting)
    )
  }

  public get nonWorkTimeAndDeadlineTitle(): string {
    return (
      this.areNonWorkAndDeadlineModalsShown &&
      Localization.translator.workflowDateTimeBlocked
    )
  }

  public get nonWorkTimeAndDeadlineMessage(): string {
    return (
      this.areNonWorkAndDeadlineModalsShown &&
      Localization.translator.pleaseMoveThisWorkflow(
        this.bookingDeadlineInterval,
      )
    )
  }

  private get isInsideDeadline(): boolean {
    if (this.template.isDeadlineHidden || !this.editablePermit?.startDate) {
      return false
    }

    const endDeadlineTime = this.projectDateStore.addWorkingDays(
      new Date(),
      this.bookingDeadlineInterval,
    )

    return isBefore(this.editablePermit.startDate, endDeadlineTime)
  }

  public moveDatesOrSetAsLate = () => {
    this.closeDeadlineModal()

    if (!this.shouldBlockLateRequesting) {
      return this.setAsLateRequest()
    }

    const { addDays, addWorkingDays, addHours, countDaysToDate, getHours } =
      this.projectDateStore

    const targetHoursDate = addHours(new Date(), THRESHOLD_DEADLINE_HOURS)
    const { startDate, endDate, isAllDay } = this.editablePermit
    const deltaInHours = getHours(targetHoursDate) - getHours(startDate)

    const totalDaysToAdd =
      this.bookingDeadlineInterval + (deltaInHours > 0 ? 1 : 0)

    const targetStartDate = addWorkingDays(targetHoursDate, totalDaysToAdd)
    const deltaInDays = countDaysToDate(startDate, targetStartDate)

    const newStartDate = addDays(startDate, deltaInDays)
    const newEndDate = addDays(endDate, deltaInDays)

    this.applyDates(newStartDate, newEndDate, isAllDay)
  }

  private unblockAndOpenModalsIfNeed = () => {
    if (this.isPermitInsideWorkTime) {
      this.hideNonWorkTimeLabel()
    }

    if (!this.isInsideDeadline) {
      this.unsetAsLateRequest()
    }

    if (
      (!this.shouldBlockOnNonWorkTimes || this.isPermitInsideWorkTime) &&
      (!this.shouldBlockLateRequesting || !this.isInsideDeadline)
    ) {
      this.unblockSubmitButton()
    }

    this.openNonWorkTimeModalIfNeed()
    this.openDeadlineModalIfNeed()
  }

  // Validate overlapping/location restrictions for selected locations and dates
  public scheduleLocationsValidation = customDebounce(
    () => this.validateLocationsRestrictions(),
    DEBOUNCE_DELAY,
  )

  // Registered by ResourceRestrictionsSync whenever the v2 resource queries
  // resolve. Replacing the map flips the ready flag so resource restriction
  // checks stop failing closed.
  @action.bound
  public setFreshResourcePermittedCompanies(map: Map<string, string[]>): void {
    this.freshResourcePermittedCompanies = map
    this.isFreshResourceDataReady = true
  }

  // Cleared on form/sync teardown so a stale map never outlives the form.
  @action.bound
  public clearFreshResourcePermittedCompanies(): void {
    this.freshResourcePermittedCompanies = new Map()
    this.isFreshResourceDataReady = false
  }

  // Set by ResourceRestrictionsSync when the materials query settles; reset on
  // teardown / project switch. See isMaterialResourceDataReady.
  @action.bound
  public setMaterialResourceDataReady(isReady: boolean): void {
    this.isMaterialResourceDataReady = isReady
  }

  // Set by ResourceRestrictionsSync when the equipment query settles; reset on
  // teardown / project switch. See isEquipmentResourceDataReady.
  @action.bound
  public setEquipmentResourceDataReady(isReady: boolean): void {
    this.isEquipmentResourceDataReady = isReady
  }

  @action.bound
  public setResourceControlFields(
    map: Map<string, { isBookable: boolean }>,
  ): void {
    this.resourceControlFieldsById = map
  }

  @action.bound
  public clearResourceControlFields(): void {
    this.resourceControlFieldsById = new Map()
  }

  @action.bound
  public setResourceWorkflowRestrictions(
    map: Map<string, IWorkflowRestrictions>,
  ): void {
    this.resourceWorkflowRestrictionsById = map
  }

  @action.bound
  public clearResourceWorkflowRestrictions(): void {
    this.resourceWorkflowRestrictionsById = new Map()
  }

  @action.bound
  public setResourceOperatingIntervals(
    map: Map<string, IMsDateInterval[]>,
  ): void {
    this.resourceOperatingIntervalsById = map
  }

  @action.bound
  public clearResourceOperatingIntervals(): void {
    this.resourceOperatingIntervalsById = new Map()
  }

  public async runLocationsValidationNow(): Promise<boolean> {
    await this.validateLocationsRestrictions()
    return this.areResourceSelectionsSubmittable
  }

  // Keep-selected is applied by the picker against its own selectedIds, so it
  // is deliberately not threaded into the predicate here.
  @computed
  public get resourceVisibilityPredicate(): (
    record: IResourceRecord,
  ) => boolean {
    return record => !!record.root?.isBookable
  }

  // Why a kept-but-now-invalid selected resource is no longer selectable,
  // shown in the picker, the form field, and used by save validation — all of
  // which have only a location id. Resolved from the registered control-fields
  // map. Ids absent from the map (pending/temp resources still being created)
  // yield null, i.e. treated as valid.
  private isResourceUnavailable(locationId: string): boolean {
    const controlFields = this.resourceControlFieldsById.get(locationId)
    return !!controlFields && !controlFields.isBookable
  }

  // Whether the fresh v2 controls for a bookable resource type have settled.
  // Pickers and gates MUST key readiness on the type they operate on: the union
  // isFreshResourceDataReady flips on the first source and would fail open for a
  // still-loading sibling type. A new bookable type fails closed here until it is
  // wired in explicitly, so it can never silently read as ready.
  public areResourceControlsReadyForType(
    resourceTypeId: ResourceTypeId,
  ): boolean {
    switch (resourceTypeId) {
      case ResourceTypeId.OFFLOADING_EQUIPMENT:
        return this.isEquipmentResourceDataReady
      case ResourceTypeId.MATERIALS:
        return this.isMaterialResourceDataReady
      // Location restrictions are still read from the legacy stores.
      case ResourceTypeId.LOCATIONS:
        return this.locationAttributesStore.isDataReceived
      default:
        return false
    }
  }

  // Location ids of a permit subject to resource-restriction validation: the
  // location/equipment field values plus selected bookable material resources
  // (which key on locationId and are absent from allLocationFieldValues).
  private bookableResourceLocationIds(
    permit: Workflow | null | undefined,
  ): string[] {
    const locationIds = (permit?.allLocationFieldValues || [])
      .map(loc => loc?.id)
      .filter((id): id is string => !!id)
    const materialIds = permit?.materialResourceLocationIds || []
    return Array.from(new Set([...locationIds, ...materialIds]))
  }

  private get allRestrictionLocationIds(): string[] {
    return this.bookableResourceLocationIds(this.editablePermit)
  }

  // Selected material resource ids. Materials keep only WR / Workflow Types /
  // Permitted Companies, so the equipment-only validations (closures, overlap)
  // skip these ids.
  @computed
  private get materialResourceLocationIdSet(): Set<string> {
    return new Set(this.editablePermit?.materialResourceLocationIds ?? [])
  }

  // A selected resource this booking can't use at all: not bookable, reserved
  // for other companies, or closed during it.
  private get isAnySelectedResourceRestricted(): boolean {
    return this.allRestrictionLocationIds.some(
      id =>
        this.isResourceUnavailable(id) ||
        this.isLocationRestrictedForCurrentCompanies(id) ||
        this.getBookedClosures(id).length > 0,
    )
  }

  private validateLocationsRestrictions = async () => {
    try {
      const startDate = this.editablePermit?.startDate
      const endDate = this.editablePermit?.endDate

      const { permitTypeId, locationIds, locationIdsToValidate } =
        this.getLocationValidationInputs()

      if (
        this.shouldSkipLocationValidation(
          startDate,
          endDate,
          permitTypeId,
          locationIds,
        )
      ) {
        // No server query here, so overlap is unavailable; gate on the
        // locally-computable restrictions only.
        this.applyViolations(
          this.isAnySelectedResourceRestricted,
          EMPTY_OVERLAP_MAP,
        )
        return
      }

      // Build time periods array (non-recurring -> single interval; recurring -> all intervals)
      const timePeriods = this.buildValidationTimePeriods(startDate, endDate)

      // Overlap is equipment-only; exclude material ids from the query.
      const equipmentLocationIds = locationIds.filter(
        id => !this.materialResourceLocationIdSet.has(id),
      )

      // Validate every selected location (not just newly-added) so overlap
      // reasons can display for already-saved locations too. permitId excludes
      // the workflow's own booking from the count, matching the picker.
      const perIntervalResults = equipmentLocationIds.length
        ? await this.validateLocationRestrictionsForPeriods(
            timePeriods,
            permitTypeId,
            equipmentLocationIds,
            this.editablePermit?.id || null,
          )
        : []

      this.applyFirstIntervalViolations(
        perIntervalResults,
        locationIdsToValidate,
        permitTypeId,
      )

      this.updateFutureIntervalsWarning(perIntervalResults)
    } catch {
      // On error, do not block to avoid false negatives
      this.resetLocationRestrictionsState()
    }
  }

  private getLocationValidationInputs(): {
    permitTypeId: string
    locationIds: string[]
    locationIdsToValidate: string[]
  } {
    const locationIds = this.bookableResourceLocationIds(this.editablePermit)
    const permitTypeId = this.editablePermit?.typeId
    const existingIds = this.existingPermit
      ? new Set(this.bookableResourceLocationIds(this.existingPermit))
      : null
    const locationIdsToValidate = existingIds
      ? locationIds.filter(id => !existingIds.has(id))
      : locationIds

    return { permitTypeId, locationIds, locationIdsToValidate }
  }

  private shouldSkipLocationValidation(
    startDate: number,
    endDate: number,
    permitTypeId: string,
    locationIds: string[],
  ): boolean {
    return !startDate || !endDate || !locationIds.length || !permitTypeId
  }

  private resetLocationRestrictionsState() {
    runInAction(() => {
      this.areLocationRestrictionsViolated = false
      this.overlapConflictsById = EMPTY_OVERLAP_MAP
      this.futureInvalidIntervals = []
      this.isFutureIntervalsRestrictionsWarningShown = false
    })
  }

  private async validateLocationRestrictionsForPeriods(
    timePeriods: Array<{ startDate: number; endDate: number }>,
    permitTypeId: string,
    locationIds: string[],
    permitId: string | null,
  ) {
    const res = await this.graphExecutorStore.query(
      ValidateLocationRestrictionsForTimePeriodsDocument,
      {
        timePeriods,
        workflowId: permitTypeId,
        locationIds,
        permitId,
      },
    )

    return res?.data?.validateLocationRestrictionsForTimePeriods || []
  }

  private applyFirstIntervalViolations(
    perIntervalResults: any[],
    locationIdsToValidate: string[],
    permitTypeId: string,
  ) {
    const firstInterval = perIntervalResults[0]
    const firstIntervalBlockedOverlaps = collectBlockedOverlaps(
      firstInterval?.locationResults,
    )
    const overlapConflicts = collectOverlapConflicts(perIntervalResults)

    // Gating overlaps stay scoped to newly-added locations, exactly as before —
    // an already-saved location's overlap must not start blocking a re-save.
    const newLocationIds = new Set(locationIdsToValidate)
    const areViolated =
      this.isAnySelectedResourceRestricted ||
      locationIdsToValidate.some(
        id => this.getExceededMaxBookingDurations(id, permitTypeId).length > 0,
      ) ||
      this.hasViolations(
        firstIntervalBlockedOverlaps.filter(o =>
          newLocationIds.has(o.locationId),
        ),
      )

    this.applyViolations(areViolated, overlapConflicts)
  }

  private hasViolations(
    violations: Array<{ locationId: string; violatedRestrictions: string[] }>,
  ): boolean {
    return violations.some(v => v.violatedRestrictions.length > 0)
  }

  // Gating (areLocationRestrictionsViolated) and the overlaps every selected
  // resource's note reads are kept apart, so an already-saved location can show
  // its overlap without that changing what blocks submission.
  private applyViolations(
    areViolated: boolean,
    overlapConflicts: Map<string, IOverlapConflict>,
  ) {
    runInAction(() => {
      this.overlapConflictsById = overlapConflicts
      this.areLocationRestrictionsViolated = areViolated
      this.futureInvalidIntervals = []
      this.isFutureIntervalsRestrictionsWarningShown = false
    })
  }

  // Builds the company-restriction inputs for a location. Bookable resources
  // (equipment, materials, …) resolve through the fresh v2 map (legacy is
  // stale); other location types use the legacy attr.
  private getCompanyRestrictionSource(
    locationId: string,
  ): ICompanyRestrictionSource {
    const attr = this.locationAttributesStore.getById(
      locationId,
    ) as LocationBase
    // An id present in the fresh map is a synced bookable resource by
    // definition (it came from a v2 resource query); the type set also catches
    // a bookable id the legacy store hasn't caught up to yet.
    const isInFreshMap = this.freshResourcePermittedCompanies.has(locationId)
    return {
      usesFreshSource:
        isInFreshMap || this.isLegacyBackedBookableResource(locationId),
      isFreshDataReady: this.isFreshResourceDataReady,
      isInFreshMap,
      freshPermitted:
        this.freshResourcePermittedCompanies.get(locationId) ?? null,
      legacyPermitted: attr?.permittedCompanies ?? null,
    }
  }

  public isLocationRestrictedForCurrentCompanies(locationId: string): boolean {
    const companyIds = this.editablePermit?.companyIds || []
    return isLocationCompanyRestricted(
      companyIds,
      this.getCompanyRestrictionSource(locationId),
    )
  }

  // The companies a resource is held for, or null when this booking may use it.
  private getReservedForCompanies(locationId: string): string[] | null {
    const companyIds = this.editablePermit?.companyIds || []
    const source = this.getCompanyRestrictionSource(locationId)
    if (!isLocationCompanyRestricted(companyIds, source)) return null

    // Empty under a pessimistic restriction (equipment data still loading, or
    // an equipment id absent from the fresh v2 result): no list to name.
    return (getPermittedCompaniesToName(source) ?? [])
      .map(id => this.companiesStore.getCompanyNameById(id))
      .filter(Boolean)
  }

  @computed
  private get closureCheckIntervals() {
    const startDate = this.editablePermit?.startDate
    const endDate = this.editablePermit?.endDate
    if (!startDate || !endDate) return []
    // Recurring settings expand to every future occurrence only while the
    // workflow is still being created (or is a draft). Once created, each
    // occurrence is its own permit with its own dates, so an existing workflow
    // checks closures against just its own interval — otherwise it would
    // surface closures that affect only sibling occurrences.
    const existing = this.existingPermit
    const isUnmaterialized = !existing || existing.isDraft
    const recurring = this.editableRecurringSettings
    // Expand to every occurrence via buildValidationTimePeriods — the same
    // expansion the overlap query uses — rather than getRecurringDates, which
    // drops occurrences outside working hours and so left non-work-hours
    // recurring workflows with no closure / operating-hours checks at all.
    return recurring && isUnmaterialized
      ? this.buildValidationTimePeriods(startDate, endDate)
      : [{ startDate, endDate }]
  }

  private getBookedClosures(locationId: string): IClosureSpan[] {
    // Equipment-only control; materials excluded.
    if (this.materialResourceLocationIdSet.has(locationId)) return []
    const typeId = this.editablePermit?.typeId
    if (!typeId || !this.closureCheckIntervals.length) return []
    // Closures store their applicable workflow types as stable `type` strings,
    // so resolve the permit's (possibly superseded) type id to its current
    // `type` before matching — passing the raw id never matches. Fall back to
    // the id so type-agnostic closures (empty specifiedPermitTypes) still show.
    const workflowType =
      this.permitTypesStore.getActualTypeByAnyVersionId(typeId)?.type ?? typeId
    const closures = this.closuresStore.getClosuresByWorkflowType(
      locationId,
      workflowType,
    )
    if (!closures.length) return []

    const intervals = this.closureCheckIntervals
    return closures
      .filter(closure =>
        intervals.some(interval =>
          this.projectDateStore.isClosedBetween(
            new Date(interval.startDate),
            new Date(interval.endDate),
            [],
            [closure.closureInterval],
            'partially',
          ),
        ),
      )
      .map(closure => closure.closureInterval)
  }

  // Whether two intervals overlap on at least one calendar day (project tz).
  /**
   * The first and last project day a range occupies, the end exclusive.
   *
   * The same arithmetic buildConcreteOperatingWindows uses to decide which days a
   * dated window governs, and the same the permit service applies to an
   * occurrence: a range finishing exactly at midnight spends no time on the day
   * it stops at, so it does not own that day. Scoping a message by any other rule
   * lets it disagree with the verdict it is explaining.
   */
  private dayRange(interval: { startDate: number; endDate?: number }): {
    first: number
    last: number
  } {
    const { startOfDay } = this.projectDateStore

    return {
      first: startOfDay(interval.startDate).getTime(),
      last: startOfDay((interval.endDate ?? interval.startDate) - 1).getTime(),
    }
  }

  private sharesDay(
    a: { startDate: number; endDate?: number },
    b: { startDate: number; endDate?: number },
  ): boolean {
    const aDays = this.dayRange(a)
    const bDays = this.dayRange(b)

    return aDays.first <= bDays.last && aDays.last >= bDays.first
  }

  /**
   * The project's own week, as one window per weekday it works.
   *
   * Weekday-scoped rather than dated: the project's hours ARE a weekly schedule,
   * so a resource inheriting them is judged the same way one stating its own week
   * is — closed on a weekday the project omits, and described as a week rather
   * than as the particular dates a booking happens to fall on.
   */
  @computed
  private get projectWeekWindows(): IMsDateInterval[] {
    return buildProjectWeekWindowsFmt(this.projectDateStore)
  }

  // Whether the occurrence runs across the given weekday on a day no dated window
  // governs, in project time.
  //
  // A booking may span several days, so it is the days it covers that decide, not
  // the weekday it happens to start on. Days a dated window covers are skipped:
  // there the dated window alone says what the resource allows, so naming the
  // weekday window too would quote hours that do not apply to the booking.
  private touchesGovernedWeekday(
    interval: { startDate: number; endDate?: number },
    dayOfWeek: number,
    datedWindows: IMsDateInterval[],
  ): boolean {
    const { startOfDay, endOfDay, addDays, getWeekdayNumber } =
      this.projectDateStore
    // End-exclusive, as everywhere else: a booking finishing at midnight spends
    // no time on the day it stops at, so that day's weekday is not one it
    // touches - naming it would send the user to hours they never booked into.
    const { first, last } = this.dayRange(interval)
    let cursor = new Date(first)

    while (cursor.getTime() <= last) {
      if (Number(getWeekdayNumber(cursor)) === dayOfWeek) {
        const day = {
          startDate: cursor.getTime(),
          endDate: endOfDay(cursor).getTime(),
        }

        if (!datedWindows.some(window => this.sharesDay(window, day))) {
          return true
        }
      }

      cursor = startOfDay(addDays(cursor, 1))
    }

    return false
  }

  // Whether every calendar day `inner` touches is also touched by `outer` (project
  // tz) — i.e. `outer` alone decides what those days allow. Checked per dated window
  // rather than across all of them, so a span several dated windows jointly cover
  // still counts the daily window as relevant: listing one window too many in the
  // message is harmless, hiding one the booking could move into is not.
  private coversEveryDay(
    outer: { startDate: number; endDate?: number },
    inner: { startDate: number; endDate?: number },
  ): boolean {
    const outerDays = this.dayRange(outer)
    const innerDays = this.dayRange(inner)

    return (
      outerDays.first <= innerDays.first && outerDays.last >= innerDays.last
    )
  }

  // Warns — never blocks — when an occurrence falls outside the location's
  // operating windows: its own daily window when it has one, and the project
  // working hours only as a FALLBACK when it doesn't. Windows resolve exactly as
  // before (withSingleDailyInterval, dated overrides, per-weekday project hours);
  // what changed is the output — a structured warning that says what is wrong
  // with the booking, which repeats it affects, and whose hours apply.
  public getHoursOfOperationWarning(
    locationId: string,
  ): IHoursOfOperationWarning | null {
    if (this.materialResourceLocationIdSet.has(locationId)) return null

    const attr = this.locationAttributesStore.getById(locationId)
    if (!attr?.type || attr.type === LocationType.ProjectLocation) return null

    if (this.resourceControlFieldsById.get(locationId)?.isBookable === false) {
      return null
    }

    if (!this.closureCheckIntervals.length) return null

    // A closure on the booking already says the resource can't be used then.
    if (this.getBookedClosures(locationId).length) return null

    const stored = this.isLegacyBackedBookableResource(locationId)
      ? (this.resourceOperatingIntervalsById.get(locationId) ?? EMPTY_INTERVALS)
      : (attr.operatingIntervals ?? EMPTY_INTERVALS)

    const resourceName = attr.name

    // A resource with no hours of its own follows the project's. The verdict
    // is then the project's own, which the date field already states once, so
    // repeating it under every such resource would only add noise.
    if (!stored.length) return null

    const operatingIntervals = withSingleDailyInterval(
      stored.map(withResolvedOperatingMode),
    )
    const recurring = operatingIntervals.filter(resolveOperatingIntervalIsDaily)

    const warning = this.buildOperatingHoursWarning(
      recurring.length ? recurring : this.projectWeekWindows,
      operatingIntervals.filter(
        window => !resolveOperatingIntervalIsDaily(window),
      ),
      !recurring.length,
    )
    return warning && { ...warning, locationId, resourceName }
  }

  @computed
  private get projectHoursFallbackWarning(): Omit<
    IHoursOfOperationWarning,
    'locationId' | 'resourceName'
  > | null {
    return this.buildOperatingHoursWarning(
      this.projectWeekWindows,
      EMPTY_INTERVALS,
      true,
    )
  }

  // The project-hours note for the date field: the same verdict and wording as
  // a resource's, judged against the project week. A project closure (holiday)
  // is not part of the week, so it falls back to naming the closed day.
  @computed
  public get projectHoursNote(): IWarningNote | null {
    const startDate = this.editablePermit?.startDate
    const endDate = this.editablePermit?.endDate
    if (!startDate || !endDate) return null

    const warning = this.projectHoursFallbackWarning
    if (warning) {
      return getHoursNote(this.projectDateStore, {
        ...warning,
        isProject: true,
        locationId: '',
        resourceName: Localization.translator.project,
      })
    }

    // Days are judged at the two ends, as the date field does
    // (getNonWorkTimeReasons), and the note names the end that is closed.
    const closedDay = [startDate, endDate].find(
      date => !this.projectDateStore.hasWorkingHours(date),
    )
    if (!closedDay) return null

    const day = this.projectDateStore.getWeekdayMonthAndDayToDisplay(closedDay)
    const { project, closedStatus, allDayX } = Localization.translator
    return {
      tone: 'warning',
      title: project,
      lines: [{ status: closedStatus, text: allDayX(day), details: [] }],
    }
  }

  // Warnings for the resources currently selected on the workflow, in field order.
  @computed
  public get hoursOfOperationWarnings(): IHoursOfOperationWarning[] {
    return Array.from(new Set(this.allRestrictionLocationIds))
      .map(id => this.getHoursOfOperationWarning(id))
      .filter(Boolean)
  }

  @computed
  public get hoursWarningNotes(): IWarningNote[] {
    return this.hoursOfOperationWarnings.map(warning =>
      getHoursNote(this.projectDateStore, warning),
    )
  }

  // One note per resource: what blocks this booking (the resource being off
  // limits, closures, its booking limits, a full calendar), or else its hours,
  // which only warn. Pickers pass the overlap from their own query.
  public getResourceNote = (
    locationId: string,
    overlapConflicts: Map<string, IOverlapConflict> = this.overlapConflictsById,
  ): IWarningNote | null => {
    const isUnavailable = this.isResourceUnavailable(locationId)
    const reservedFor = this.getReservedForCompanies(locationId)
    const overlap = overlapConflicts.get(locationId)
    const closures = this.getBookedClosures(locationId)
    const typeId = this.editablePermit?.typeId
    const maxDurations = typeId
      ? this.getExceededMaxBookingDurations(locationId, typeId)
      : []
    if (
      isUnavailable ||
      reservedFor ||
      closures.length ||
      maxDurations.length ||
      overlap
    ) {
      return getBlockedNote(
        this.projectDateStore,
        this.locationAttributesStore.getById(locationId)?.name,
        {
          isUnavailable,
          reservedFor: reservedFor ?? undefined,
          closures,
          maxDurations,
          overlap,
        },
      )
    }
    const warning = this.getHoursOfOperationWarning(locationId)
    return warning && getHoursNote(this.projectDateStore, warning)
  }

  private buildOperatingHoursWarning(
    fallbackWindows: IMsDateInterval[],
    datedWindows: IMsDateInterval[],
    isProjectHours: boolean,
  ): Omit<IHoursOfOperationWarning, 'locationId' | 'resourceName'> | null {
    const intervals = this.closureCheckIntervals
    const allowedWindows: IMsDateInterval[] = [
      ...fallbackWindows,
      ...datedWindows,
    ]
    const isAllDay = !!this.editablePermit?.isAllDay

    const failures = intervals.flatMap(interval => {
      if (
        this.projectDateStore.isIntervalWithinOperatingWindows(
          interval.startDate,
          interval.endDate,
          allowedWindows,
        )
      ) {
        return []
      }
      const problem = explainOccurrence(
        this.projectDateStore,
        interval,
        allowedWindows,
        isAllDay,
      )
      return problem ? [{ interval, problem }] : []
    })
    if (!failures.length) return null

    const failingIntervals = failures.map(failure => failure.interval)

    const relevantDatedWindows = datedWindows.filter(operating =>
      failingIntervals.some(interval => this.sharesDay(operating, interval)),
    )
    const isDailyWindowRelevant = failingIntervals.some(
      interval =>
        !datedWindows.some(operating =>
          this.coversEveryDay(operating, interval),
        ),
    )
    const relevantFallbackWindows = fallbackWindows.filter(window => {
      // A weekday window applies to one day of the week, so it is named only when
      // a failing occurrence actually lands on that day.
      if (isWeekdayScopedInterval(window)) {
        return failingIntervals.some(interval =>
          this.touchesGovernedWeekday(interval, window.dayOfWeek, datedWindows),
        )
      }
      return window.isDaily
        ? isDailyWindowRelevant
        : failingIntervals.some(interval => this.sharesDay(window, interval))
    })
    const scoped = [...relevantFallbackWindows, ...relevantDatedWindows]
    const relevantWindows = scoped.length ? scoped : fallbackWindows

    const weekdayLabels = buildWeekdayScheduleRows(
      this.projectDateStore,
      relevantWindows
        .filter(isWeekdayScopedInterval)
        .map(({ dayOfWeek, startDate, endDate }) => ({
          dayOfWeek,
          startDate,
          endDate: endDate ?? startDate,
        })),
    ).map(
      ({ label, endsNextDay }) =>
        `${label}${endsNextDay ? ` ${Localization.translator.nextDay}` : ''}`,
    )
    const otherLabels = Array.from(
      new Set(
        relevantWindows
          .filter(window => !isWeekdayScopedInterval(window))
          .map(operating =>
            formatOperatingIntervalFmt(this.projectDateStore, operating),
          ),
      ),
    )
    const hoursLabels = [...weekdayLabels, ...otherLabels]

    return {
      isProjectHours,
      hoursLabels: hoursLabels.length
        ? hoursLabels
        : [Localization.translator.noneSet],
      problem: failures[0].problem,
      failingCount: failures.length,
      totalCount: intervals.length,
      firstFailingStart: failures[0].interval.startDate,
    }
  }

  @computed
  public get locationTimePeriods(): Array<{
    startDate: number
    endDate: number
  }> {
    const startDate = this.editablePermit?.startDate
    const endDate = this.editablePermit?.endDate
    if (!startDate || !endDate) return EMPTY_PERIODS
    return this.buildValidationTimePeriods(startDate, endDate)
  }

  private updateFutureIntervalsWarning(perIntervalResults: any[]) {
    const hasRecurring = !!this.workflowFieldsStore.editableRecurringSettings
    const failedFuture =
      hasRecurring && perIntervalResults.length > 1
        ? perIntervalResults
            .slice(1)
            .filter(r => r && r.isValid === false)
            .map(r => ({
              startDate: r.timePeriod?.startDate,
              endDate: r.timePeriod?.endDate,
            }))
            .filter(i => i.startDate && i.endDate)
        : []

    runInAction(() => {
      this.futureInvalidIntervals = failedFuture
      this.isFutureIntervalsRestrictionsWarningShown = failedFuture.length > 0
    })
  }

  private buildValidationTimePeriods(
    baseStartDate: number,
    baseEndDate: number,
  ): Array<{ startDate: number; endDate: number }> {
    const settings = this.workflowFieldsStore.editableRecurringSettings
    // Non-recurring: single interval
    if (!settings) {
      return [{ startDate: baseStartDate, endDate: baseEndDate }]
    }

    const { frequency, frequencyType, endDate: recurringEndDate } = settings
    const durationMs = baseEndDate - baseStartDate
    const startTimeMs = this.projectDateStore.getTimeMs(baseStartDate)

    if (frequencyType === CalendricalType.Day) {
      return this.buildDailyPeriods(
        baseStartDate,
        durationMs,
        recurringEndDate,
        frequency,
      )
    }

    if (frequencyType === CalendricalType.Week) {
      return this.buildWeeklyPeriods(
        baseStartDate,
        durationMs,
        startTimeMs,
        recurringEndDate,
        frequency,
        settings.selectedDays || [],
      )
    }

    // Fallback to single interval for unsupported types
    return [{ startDate: baseStartDate, endDate: baseEndDate }]
  }

  private buildDailyPeriods(
    baseStartDate: number,
    durationMs: number,
    recurringEndDate: number,
    frequency: number,
  ): Array<{ startDate: number; endDate: number }> {
    const periods: Array<{ startDate: number; endDate: number }> = []
    let currentStart = new Date(baseStartDate)
    while (!isAfter(currentStart, recurringEndDate)) {
      const start = currentStart.getTime()
      periods.push({ startDate: start, endDate: start + durationMs })
      currentStart = this.projectDateStore.addDays(currentStart, frequency)
    }
    return periods
  }

  private buildWeeklyPeriods(
    baseStartDate: number,
    durationMs: number,
    startTimeMs: number,
    recurringEndDate: number,
    frequency: number,
    selectedDays: number[],
  ): Array<{ startDate: number; endDate: number }> {
    const periods: Array<{ startDate: number; endDate: number }> = []

    const daysToUse = (selectedDays || [])
      .map(Number)
      .filter(Boolean)
      .sort((a, b) => a - b)

    // If nothing selected (shouldn't happen via UI), default to base start weekday
    const fallbackDay = Number(
      this.projectDateStore.getWeekdayNumber(baseStartDate),
    )
    const normalizedDays = daysToUse.length ? daysToUse : [fallbackDay]

    let weekStart = this.projectDateStore.startOfWeek(baseStartDate)
    while (!isAfter(weekStart, recurringEndDate)) {
      for (const dayNum of normalizedDays) {
        const dayDate = this.projectDateStore.addDays(weekStart, dayNum - 1)
        // Align time to baseStartDate time
        const occurrenceStart = this.projectDateStore
          .setTimeMs(dayDate, startTimeMs)
          .getTime()
        if (occurrenceStart < baseStartDate) continue
        if (occurrenceStart > recurringEndDate) continue
        periods.push({
          startDate: occurrenceStart,
          endDate: occurrenceStart + durationMs,
        })
      }
      weekStart = this.projectDateStore.addWeeks(weekStart, frequency)
    }
    // Ensure the first interval is always at index 0 (base interval)
    periods.sort((a, b) => a.startDate - b.startDate)
    return periods
  }

  @action.bound
  public closeFutureIntervalsRestrictionsWarning() {
    this.isFutureIntervalsRestrictionsWarningShown = false
  }

  private getExceededMaxBookingDurations(
    locationId: string,
    permitTypeId: string,
  ): IExceededMaxDuration[] {
    const startDate = this.editablePermit?.startDate
    const endDate = this.editablePermit?.endDate
    if (!startDate || !endDate) {
      return []
    }
    const bookingMs = endDate - startDate
    const location = this.locationAttributesStore.getById(
      locationId,
    ) as LocationAttributeBase
    // Prefer the fresh v2 map (fed by ResourceRestrictionsSync for every
    // bookable resource type, incl. equipment) over the legacy attr store,
    // which is stale for these; fall back to legacy only for ids absent from
    // the fresh map. Materials live only in the fresh map.
    const workflowRestrictions =
      this.resourceWorkflowRestrictionsById.get(locationId) ??
      location?.workflowRestrictions
    if (!workflowRestrictions?.maxBookingDurations) {
      return []
    }
    return workflowRestrictions.maxBookingDurations
      .filter(r =>
        this.permitTypesStore.isPermitTypeMatchingWorkflowIds(
          permitTypeId,
          r.workflowIds,
        ),
      )
      .map(r => ({
        limit: {
          duration: Number(r.values[0]),
          unit:
            (r.values[1] as WorkflowDurationUnit) || WorkflowDurationUnit.Hours,
        },
        bookingMs,
      }))
      .filter(
        ({ limit }) =>
          durationToMilliseconds({
            duration: limit.duration,
            durationType: WorkflowDurationType.Time,
            durationUnit: limit.unit,
          }) < bookingMs,
      )
  }

  public get isRecurringWorkflowEnabled() {
    return this.template?.recurringOptions?.isEnabled || false
  }

  public get maxRecurringDuration(): number {
    return this.template?.recurringOptions?.maxDuration ?? 0
  }

  public get isRecurringSettingsChanged(): boolean {
    return this.workflowFieldsStore.isRecurringSettingsChanged
  }

  @action.bound
  // main is LAST: prepareForCreate emits [main, ...children].reverse().
  private mainCreatedPermit(permits: ISitePermit[]): ISitePermit {
    return permits[permits.length - 1]
  }

  private handlePermitCreateSuccess = (
    result: IRecurringWorkflowCreationResult | ISitePermit[],
  ) => {
    if (!result) {
      const traceId = generateTraceId()
      Sentry.captureMessage(
        'Recurring workflow creation returned null result',
        {
          level: 'error',
          tags: { struxhub_trace_id: traceId },
        },
      )
      toast.error(Localization.translator.failedToCreateRecurringWorkflows, {
        timeout: 10000,
        description: React.createElement(TraceIdDisplay, { traceId }),
      })
      this.handleCreateError()
      return
    }

    const isNonRecurring = Array.isArray(result)
    const permits = isNonRecurring ? result : (result.createdWorkflows ?? [])
    const blockedWorkflows = isNonRecurring
      ? []
      : (result.blockedWorkflows ?? [])

    if (permits.length > 0 && this.pendingUploadLogs.length > 0) {
      this.updatePendingUploadLogs(permits)
    }
    if (permits.length > 0) {
      this.markupsStore.flushPending(
        this.markupDraftId,
        this.mainCreatedPermit(permits).id,
      )
    } else {
      this.discardPendingMarkups()
    }
    this.resetRecurringSettingsIfNeed()
    this.showToastForDraft(permits[0])
    this.showSkippedWorkflowsToast(permits.length, blockedWorkflows)
    if (permits.length > 0) {
      this.onCreateSuccess?.(permits)
    } else {
      this.onCreateError?.()
    }
  }

  private handleCreateError = () => {
    // saveWorkflow closes the form as soon as it dispatches, so by the time this runs there
    // is nothing to retry in: discarding here would delete the buffer for good. A map-first
    // buffer goes back to the hand-off slot — the next creation form adopts it, and its own
    // teardown drops it if the user gives up. A form-private buffer has nowhere to return
    // to, so it is dropped as before.
    if (this.adoptedMarkupDraftId && this.eventsStore?.appState) {
      this.eventsStore.appState.preSelectedMarkupDraftId =
        this.adoptedMarkupDraftId
    } else {
      this.discardPendingMarkups()
    }
    this.onCreateError?.()
  }

  private showSkippedWorkflowsToast = (
    createdCount: number,
    blockedWorkflows: Array<IBlockedWorkflowGroup | null>,
  ) => {
    const validBlocked = blockedWorkflows.filter(
      (item): item is IBlockedWorkflowGroup => item !== null,
    )
    if (validBlocked.length === 0) return

    const { translator } = Localization
    const totalCount = createdCount + validBlocked.length
    const skipReasons: string[] = []

    // Single-pass categorization
    const closuresByLocation = new Map<string, number>()
    const operatingHoursByLocation = new Map<string, number>()
    let projectClosedCount = 0
    let nonWorkingDayCount = 0

    for (const blocked of validBlocked) {
      switch (blocked.reason) {
        case BlockingReason.Closure:
          const key = blocked.locationName || translator.unknownLocation
          closuresByLocation.set(key, (closuresByLocation.get(key) || 0) + 1)
          break
        case BlockingReason.OutsideOperatingHours:
          const operatingKey =
            blocked.locationName || translator.unknownLocation
          operatingHoursByLocation.set(
            operatingKey,
            (operatingHoursByLocation.get(operatingKey) || 0) + 1,
          )
          break
        case BlockingReason.ProjectClosedInterval:
          projectClosedCount++
          break
        case BlockingReason.NonWorkingDay:
          nonWorkingDayCount++
          break
      }
    }

    // Build skip reasons
    closuresByLocation.forEach((count, locationName) => {
      skipReasons.push(
        `${count} ${translator.blockedByClosure} "${locationName}"`,
      )
    })
    operatingHoursByLocation.forEach((count, locationName) => {
      skipReasons.push(
        `${count} ${translator.blockedByOperatingHours} "${locationName}"`,
      )
    })
    if (projectClosedCount > 0) {
      skipReasons.push(
        `${projectClosedCount} ${translator.blockedByProjectClosed}`,
      )
    }
    if (nonWorkingDayCount > 0) {
      skipReasons.push(
        `${nonWorkingDayCount} ${translator.blockedByNonWorkingDay}`,
      )
    }

    const message = `${translator.createdXofYWorkflows(
      createdCount,
      totalCount,
    )}. ${translator.skippedWorkflows}: ${skipReasons.join(', ')}`

    toast.success(message, { timeout: 8000 })
  }

  @action.bound
  private async updatePendingUploadLogs(permits: ISitePermit[]) {
    if (this.pendingUploadLogs.length === 0) {
      return
    }

    const parentPermit = this.mainCreatedPermit(permits)

    try {
      // Update each upload log with permit information using the tracked IDs
      for (const { workflowId, uploadLog } of this.pendingUploadLogs) {
        const workflowToSave =
          workflowId === CURRENT_PERMIT_ID
            ? parentPermit
            : permits.find(permit => permit.id === workflowId)
        const updateInput: IUploadLogInput = {
          ...toUploadLogInput(uploadLog),
          sourceEntityId: workflowToSave.id,
          sourceEntityCode: workflowToSave.code,
          sourceEntityTypeName: workflowToSave.initialType,
          workflowType: workflowToSave.typeId,
          workflowCategory: workflowToSave.categoryId,
          isHidden: workflowToSave.status === SitePermitStatus.Draft,
        }

        await this.graphExecutorStore.mutate(SaveUploadLogDocument, {
          uploadLog: updateInput,
        })
      }

      // Clear pending IDs after successful update
      runInAction(() => {
        this.pendingUploadLogs = []
      })
    } catch (error) {
      console.error('Failed to update upload logs:', error)
    }
  }

  public setRecurringSettings = (settings: RecurringWorkflowSettings) => {
    this.workflowFieldsStore.setRecurringSettings(settings)
    // Re-validate locations since recurring intervals affect validation periods
    this.scheduleLocationsValidation()
  }

  public resetRecurringSettingsIfNeed = () => {
    if (
      this.workflowFieldsStore.recurringSettings &&
      !this.editablePermit.recurringSettingsId
    ) {
      this.workflowFieldsStore.setRecurringSettings(null)
    }
  }

  public setPreviewTemplate = (template: WorkflowType) => {
    this.workflowFieldsStore.setPreviewTemplate(template)
  }

  private showToastForDraft = (savedPermit: ISitePermit) => {
    if (savedPermit?.status === SitePermitStatus.Draft) {
      toast.success(Localization.translator.draftSavedSuccessfully)
    }
  }
}
